// BUILTIN_CLASS_EXTENSIONS_FOCUS_V2
// CLASS_WIDE_ASSIGNMENT_EXTENSION_V1
// ASSIGNMENTS_FLOATING_HORIZONTAL_SCROLL_WORKING_V3
// ASSIGNMENTS_FLOATING_HORIZONTAL_SCROLL_V2
// ASSIGNMENTS_TOP_HORIZONTAL_SCROLL_V1
// ASSIGNMENTS_SINGLE_SCROLL_WORKSPACE_V1
// SCHEDULE_MODEL_PHASE10_20260911
import {
  getEffectiveNow,
  getEffectiveToday
} from '../../../utils/systemTime';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import GradingPanel from './GradingPanel';
import {
  selfEvalReviewIsComplete,
  selfEvalCompleteGradeLabel
} from '../../../utils/selfEvalReview';
import { notifyReleasedIncomplete } from '../../../utils/incompleteGradeNotifications';
import QuestionImprovement from './QuestionImprovement';
import {
  assignQiQuestionsForWeek
} from '../../../utils/qiAssignments';
import {
  getScheduleItemChronologyValue,
  getScheduleItemDates,
  getScheduleItemSource,
  isScheduleChoiceEnabledForAssignment,
  loadStudentAssignmentScheduleContext
} from '../../../utils/studentAssignmentSchedule';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function getBuiltInTypes(course) {
  const types = [];
  if (course.attendance_enabled !== false) types.push('Attendance');
  if (course.cq_enabled !== false) types.push('Critical Questions');
  if (course.qi_enabled === true) types.push('Question Improvement');
  if (course.pd_enabled !== false) types.push('Philosophical Diary');
  if (course.self_eval_enabled !== false) types.push('Self-Evaluation');
  return types;
}
function apiHeaders() {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };
}

async function readJson(response, label) {
  if (!response.ok) {
    throw new Error(`${label} failed (${response.status})`);
  }
  return response.json();
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading weeks');
}

async function fetchEnrolments(
  courseId,
  includeMaster = false
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=*,students(id,student_code,english_name,chinese_name,is_master,photo_drive_file_id)`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading enrolments'
  );

  return (Array.isArray(rows) ? rows : [])
    .filter(
      enrolment =>
        includeMaster ||
        enrolment?.students?.is_master !== true
    )
    .sort((a, b) =>
      String(a?.students?.student_code || '')
        .localeCompare(
          String(b?.students?.student_code || ''),
          undefined,
          {
            numeric: true,
            sensitivity: 'base'
          }
        )
    );
}

async function fetchCourseInstructors(courseId) {
  const headers = apiHeaders();

  const [courseResponse, accessResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=instructor_id&limit=1`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&select=instructor_id,instructors(id,name,email)`,
      { headers }
    )
  ]);

  const courseRows = await readJson(courseResponse, 'Loading primary instructor');
  const accessRows = await readJson(accessResponse, 'Loading secondary instructors');
  const primaryId = courseRows?.[0]?.instructor_id || null;

  let primaryInstructor = null;
  if (primaryId) {
    const primaryResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/instructors?id=eq.${primaryId}&select=id,name,email&limit=1`,
      { headers }
    );
    const primaryRows = await readJson(primaryResponse, 'Loading primary instructor account');
    primaryInstructor = primaryRows?.[0] || null;
  }

  const secondaries = (Array.isArray(accessRows) ? accessRows : [])
    .map(row => row.instructors)
    .filter(Boolean)
    .filter(instructor => instructor.id !== primaryId)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return [primaryInstructor, ...secondaries].filter(Boolean);
}

async function fetchAssignmentCompletions(enrolmentIds) {
  if (!enrolmentIds) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_completions?enrolment_id=in.(${enrolmentIds})&select=*`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment completions');
}

async function saveAssignmentCompletion(enrolmentId, courseAssignmentId, completed) {
  const existingResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_completions?enrolment_id=eq.${enrolmentId}&course_assignment_id=eq.${courseAssignmentId}&select=*`,
    { headers: apiHeaders() }
  );

  const existingRows = await readJson(
    existingResponse,
    'Checking assignment completion'
  );

  if (existingRows.length > 0) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/assignment_completions?id=eq.${existingRows[0].id}`,
      {
        method: 'PATCH',
        headers: {
          ...apiHeaders(),
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({ completed })
      }
    );
    return readJson(response, 'Saving assignment completion');
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_completions`,
    {
      method: 'POST',
      headers: {
        ...apiHeaders(),
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        enrolment_id: enrolmentId,
        course_assignment_id: courseAssignmentId,
        completed
      })
    }
  );

  return readJson(response, 'Creating assignment completion');
}

function getInstructorInitials(name) {
  const text = String(name || '').trim();

  return text
    .split(/\s+/)
    .filter(Boolean)
    .map(part =>
      part.charAt(0).toUpperCase()
    )
    .join('')
    .slice(0, 3) || '?';
}

const INSTRUCTOR_BADGES = [
  { background: '#dceefe', color: '#174d72' },
  { background: '#e8e1f8', color: '#533e84' },
  { background: '#e4f2e7', color: '#2f6a42' },
  { background: '#f8eadf', color: '#82512c' },
  { background: '#f6e2ea', color: '#7c4057' }
];

async function fetchAttendance(enrolmentIds) {
  if (!enrolmentIds) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance?enrolment_id=in.(${enrolmentIds})&select=*`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading attendance');
}

async function fetchAttendanceGroupContext(courseId) {
  const [membershipsResponse, weeksResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_memberships?select=enrolment_id,attendance_group_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
      { headers: apiHeaders() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_weeks?select=attendance_group_id,course_week_id,start_time_override,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
      { headers: apiHeaders() }
    )
  ]);

  const [memberships, groupWeeks] = await Promise.all([
    readJson(membershipsResponse, 'Loading attendance group memberships'),
    readJson(weeksResponse, 'Loading attendance group schedule')
  ]);

  return { memberships, groupWeeks };
}

async function fetchCqSubmissions(enrolmentIds) {
  if (!enrolmentIds) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=in.(${enrolmentIds})&select=*`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading critical questions');
}

async function fetchCqManualSubmissionMarks(
  enrolmentIds
) {
  if (!enrolmentIds) return [];

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_manual_submission_marks?enrolment_id=in.(${enrolmentIds})&select=*`,
      {
        headers:
          apiHeaders()
      }
    );

  return readJson(
    response,
    'Loading manually marked Critical Questions'
  );
}


async function saveCqManualSubmissionMark(
  enrolmentId,
  weekId,
  instructorId
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_manual_submission_marks?on_conflict=enrolment_id,week_id`,
      {
        method: 'POST',

        headers: {
          ...apiHeaders(),

          'Content-Type':
            'application/json',

          'Prefer':
            'resolution=merge-duplicates,return=representation'
        },

        body:
          JSON.stringify({
            enrolment_id:
              enrolmentId,

            week_id:
              weekId,

            marked_by:
              instructorId ||
              null,

            marked_at:
              new Date()
                .toISOString()
          })
      }
    );

  return readJson(
    response,
    'Marking Critical Question submitted'
  );
}


async function deleteCqManualSubmissionMark(
  enrolmentId,
  weekId
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_manual_submission_marks?enrolment_id=eq.${encodeURIComponent(
        enrolmentId
      )}&week_id=eq.${encodeURIComponent(
        weekId
      )}`,
      {
        method: 'DELETE',
        headers:
          apiHeaders()
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Removing manual Critical Question mark failed (${response.status}): ${detail}`
    );
  }
}

async function fetchPdSubmissions(enrolmentIds) {
  if (!enrolmentIds) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions?enrolment_id=in.(${enrolmentIds})&select=*,pd_feedback(*)`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading philosophical diaries');
}

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(id,name,instructions,requires_submission,assignment_kind,group_submission)&order=instance_number.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading course assignments');
}

async function fetchStructuredFormInstances(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instances?select=*,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}&order=instance_order.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading Structured Form instances');
}

async function fetchAssignmentSubmissions(enrolmentIds) {
  if (!enrolmentIds) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=in.(${enrolmentIds})&select=*,assignment_feedback(*)`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment submissions');
}

async function fetchInstructorFeedbackForGradingGrid(
  pdSubmissions,
  assignmentSubmissions
) {
  const pdIds = [
    ...new Set(
      (pdSubmissions || [])
        .map(row => row?.id)
        .filter(Boolean)
        .map(String)
    )
  ];

  const assignmentIds = [
    ...new Set(
      (assignmentSubmissions || [])
        .map(row => row?.id)
        .filter(Boolean)
        .map(String)
    )
  ];

  const requests = [];

  /*
   * PD loading is deliberately left on the existing table/query.
   */
  if (pdIds.length) {
    requests.push(
      fetch(
        SUPABASE_URL +
          '/rest/v1/instructor_submission_feedback' +
          '?submission_type=eq.pd' +
          '&submission_id=in.(' +
          pdIds.join(',') +
          ')' +
          '&select=id,submission_type,submission_id,instructor_id,completion_status,score,submitted,submitted_at,created_at,updated_at',
        {
          headers:
            apiHeaders()
        }
      ).then(response =>
        readJson(
          response,
          'Loading PD instructor feedback'
        )
      )
    );
  }

  if (assignmentIds.length) {
    requests.push(
      fetch(
        SUPABASE_URL +
          '/rest/v1/rpc/get_instructor_submission_feedback_for_grading',
        {
          method: 'POST',
          headers: {
            ...apiHeaders(),
            'Content-Type':
              'application/json'
          },
          body: JSON.stringify({
            p_submission_type:
              'assignment',
            p_submission_ids:
              assignmentIds
          })
        }
      ).then(response =>
        readJson(
          response,
          'Loading assignment grading'
        )
      )
    );
  }

  if (!requests.length) {
    return [];
  }

  const groups =
    await Promise.all(requests);

  return groups.flatMap(
    rows =>
      Array.isArray(rows)
        ? rows
        : []
  );
}


async function fetchUnpublishedGradingStatus(
  courseId
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/get_unpublished_grading_status',
      {
        method: 'POST',
        headers: {
          ...apiHeaders(),
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_course_id:
            courseId
        })
      }
    );

  return readJson(
    response,
    'Loading unpublished grading status'
  );
}


async function publishCourseAssignmentGrades(
  courseAssignmentId
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/publish_course_assignment_grades',
      {
        method: 'POST',
        headers: {
          ...apiHeaders(),
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_course_assignment_id:
            courseAssignmentId
        })
      }
    );

  return readJson(
    response,
    'Publishing grades'
  );
}

async function fetchAssignmentScheduleSettings(courseId) {
  const response = await fetch(
   `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=course_id,assignment_type_id,display_name,schedule_enabled,schedule_choice_enabled,schedule_shared_across_instances,schedule_open_offset_days,schedule_due_offset_days,group_deadlines_enabled,menu_order`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment Schedule settings');
}

async function fetchAssignmentScheduleSelections(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_selections?select=course_assignment_id,course_assignment_schedule_item_id,enrolment_id,group_number,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment Schedule selections');
}

async function fetchAssignmentGroupMembershipsForCourse(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?select=course_assignment_id,enrolment_id,group_number,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment group memberships');
}

async function fetchStructuredFormGroupWorkspaces(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/structured_form_group_workspaces?select=id,course_assignment_id,group_number,owner_enrolment_id,working_submission_id,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading Structured Form group workspaces'
  );
}

async function fetchStructuredFormGroupDeadlines(
  instanceIds
) {
  const ids = (Array.isArray(instanceIds)
    ? instanceIds
    : []
  ).filter(Boolean);

  if (!ids.length) return [];

  const inFilter =
    '(' +
    ids.map(encodeURIComponent).join(',') +
    ')';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_group_deadlines?course_structured_instance_id=in.${inFilter}&select=*`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading Structured Form group deadlines'
  );
}

async function fetchStructuredFormScheduleItems(
  instanceIds
) {
  const ids = (Array.isArray(instanceIds)
    ? instanceIds
    : []
  ).filter(Boolean);

  if (!ids.length) return [];

  const inFilter =
    '(' +
    ids.map(encodeURIComponent).join(',') +
    ')';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items?course_structured_instance_id=in.${inFilter}&select=*&order=display_order.asc,created_at.asc`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading Structured Form Schedule items'
  );
}

async function fetchStructuredFormScheduleSelections(
  instanceIds
) {
  const ids = (Array.isArray(instanceIds)
    ? instanceIds
    : []
  ).filter(Boolean);

  if (!ids.length) return [];

  const inFilter =
    '(' +
    ids.map(encodeURIComponent).join(',') +
    ')';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?course_structured_instance_id=in.${inFilter}&select=*`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading Structured Form Schedule selections'
  );
}

async function fetchSelfEvalResponses(enrolmentIds) {
  if (!enrolmentIds) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_responses?enrolment_id=in.(${enrolmentIds})&select=*`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading self-evaluations');
}

async function fetchQiData(weekIds) {
  if (!weekIds) return { assignments: [], submissions: [] };

  const [assignmentResponse, submissionResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_assignments?course_week_id=in.(${weekIds})&select=*`,
      { headers: apiHeaders() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_improvements?select=*,cq_question_assignments!inner(course_week_id)&cq_question_assignments.course_week_id=in.(${weekIds})`,
      { headers: apiHeaders() }
    )
  ]);

  const [assignments, submissions] = await Promise.all([
    readJson(assignmentResponse, 'Loading QI assignments'),
    readJson(submissionResponse, 'Loading QI submissions')
  ]);

  return { assignments, submissions };
}

async function saveAttendance(enrolmentId, weekId, present) {
  const existingResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance?enrolment_id=eq.${enrolmentId}&week_id=eq.${weekId}&select=id&limit=1`,
    { headers: apiHeaders() }
  );

  const existingRows = await readJson(
    existingResponse,
    'Checking attendance'
  );

  const payload = {
    present,
    check_in_source: 'instructor_manual',
    checked_in_at: null,
    effective_check_in_at: null,
    is_test: false,
    marked_at: new Date().toISOString()
  };

  if (existingRows.length > 0) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/attendance?id=eq.${existingRows[0].id}`,
      {
        method: 'PATCH',
        headers: {
          ...apiHeaders(),
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(payload)
      }
    );

    return readJson(
      response,
      'Updating attendance'
    );
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance`,
    {
      method: 'POST',
      headers: {
        ...apiHeaders(),
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        enrolment_id: enrolmentId,
        week_id: weekId,
        ...payload
      })
    }
  );

  return readJson(
    response,
    'Creating attendance'
  );
}

function getStudentName(student) {
  const names = [
    student?.english_name,
    student?.chinese_name
  ].filter(Boolean);

  return (
    names.join(' / ') ||
    student?.student_code ||
    '—'
  );
}

function getStudentIdentity(student) {
  const code = student?.student_code || '—';
  return `${code} — ${getStudentName(student)}`;
}

function formatCqReceivedAt(value) {
  if (!value) return '';

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  return new Intl.DateTimeFormat(
    'en-GB',
    {
      timeZone:
        'Asia/Taipei',

      day:
        'numeric',

      month:
        'short',

      year:
        'numeric',

      hour:
        '2-digit',

      minute:
        '2-digit',

      second:
        '2-digit',

      hourCycle:
        'h23'
    }
  ).format(date);
}


function formatAttendanceSheetWeek(week) {
  if (!week) return 'Week';

  const date = new Date(
    `${String(week.class_date || '').slice(0, 10)}T12:00:00`
  );

  const dateLabel = Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric'
      });

  return [
    `Week ${week.week_number}`,
    dateLabel
  ].filter(Boolean).join(', ');
}

function escapeAttendanceHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function safeAttendanceFilePart(value) {
  return String(value || 'Attendance')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

async function printAttendanceSheetHtml(
  html,
  fileName
) {
  const frame = document.createElement('iframe');

  frame.setAttribute('aria-hidden', 'true');

  Object.assign(frame.style, {
    position: 'fixed',
    width: '210mm',
    height: '297mm',
    border: '0',
    left: '-10000px',
    top: '0',
    pointerEvents: 'none'
  });

  document.body.appendChild(frame);

  const printDocument = frame.contentDocument;
  const printWindow = frame.contentWindow;

  if (!printDocument || !printWindow) {
    frame.remove();

    throw new Error(
      'The browser could not create the attendance print document.'
    );
  }

  const title = String(fileName || 'Attendance')
    .replace(/\.pdf$/i, '');

  printDocument.open();

  printDocument.write(`
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeAttendanceHtml(title)}</title>

  <style>
    @page {
      size: A4 portrait;
      margin: 10mm;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family:
        Arial,
        "Noto Sans TC",
        "Microsoft JhengHei",
        sans-serif;
      font-size: 10.5pt;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    section {
      display: block;
      width: 100%;
    }

    h1 {
      margin: 0 0 4px;
      font-size: 18px;
      line-height: 1.2;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 11px;
    }

    thead {
      display: table-header-group;
    }

    tfoot {
      display: table-footer-group;
    }

    tr {
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    th,
    td {
      border: 1px solid #555;
      padding: 7px 6px;
      text-align: left;
      vertical-align: middle;
      height: 24px;
      box-sizing: border-box;
    }

    th {
      background: #f1f3f5 !important;
      font-weight: 700;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    tbody td {
      min-height: 24px;
    }
  </style>
</head>

<body>
  ${html}
</body>
</html>
  `);

  printDocument.close();

  await new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

  let cleaned = false;

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;

    window.setTimeout(
      () => frame.remove(),
      100
    );
  };

  printWindow.addEventListener(
    'afterprint',
    cleanup,
    { once: true }
  );

  const previousDocumentTitle =
    document.title;

  document.title =
    title;

  printWindow.focus();

  try {
    printWindow.print();
  } finally {
    /*
     * Keep the content-specific title long enough
     * for the browser print/save dialog to capture it,
     * then restore the application title.
     */
    window.setTimeout(
      () => {
        document.title =
          previousDocumentTitle;
      },
      1000
    );
  }

  // Fallback for browsers that do not fire afterprint on an iframe.
  window.setTimeout(cleanup, 60000);
}

async function printClarificatoryQuestionsHtml(
  html,
  fileName
) {
  const frame =
    document.createElement(
      'iframe'
    );

  frame.setAttribute(
    'aria-hidden',
    'true'
  );

  Object.assign(
    frame.style,
    {
      position: 'fixed',
      width: '297mm',
      height: '210mm',
      border: '0',
      left: '-10000px',
      top: '0',
      pointerEvents: 'none'
    }
  );

  document.body.appendChild(
    frame
  );

  const printDocument =
    frame.contentDocument;

  const printWindow =
    frame.contentWindow;

  if (
    !printDocument ||
    !printWindow
  ) {
    frame.remove();

    throw new Error(
      'The browser could not create the Clarificatory Questions print document.'
    );
  }

  const title =
    String(
      fileName ||
      'Clarificatory Questions'
    ).replace(
      /\.pdf$/i,
      ''
    );

  printDocument.open();

  printDocument.write(`
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />

  <title>${escapeAttendanceHtml(
    title
  )}</title>

  <style>
    @page {
      size: A4 landscape;
      margin: 12mm;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;

      font-family:
        Arial,
        "Noto Sans TC",
        "Microsoft JhengHei",
        sans-serif;

      -webkit-print-color-adjust:
        exact;

      print-color-adjust:
        exact;
    }

    .question-page {
      width: 100%;
      height: 180mm;

      box-sizing:
        border-box;

      display: flex;
      flex-direction: column;

      break-after: page;
      page-break-after:
        always;
    }

    .question-page:last-child {
      break-after: auto;
      page-break-after:
        auto;
    }

    .question-header {
      flex: 0 0 auto;

      display: flex;
      justify-content:
        space-between;
      align-items:
        center;
      gap: 12px;

      color: #555;

      font-size: 11pt;
      font-weight: 600;

      padding-bottom: 6mm;
    }

    .question-body {
      flex: 1 1 auto;

      display: flex;
      align-items:
        center;
      justify-content:
        center;

      text-align: center;

      padding:
        8mm 14mm;

      box-sizing:
        border-box;

      overflow:
        hidden;
    }

    .question-text {
      max-width: 245mm;

      line-height: 1.35;

      overflow-wrap:
        anywhere;

      white-space:
        normal;
    }

    .question-footer {
      flex: 0 0 auto;

      text-align: right;

      color: #777;

      font-size: 9pt;

      padding-top: 5mm;
    }
  </style>
</head>

<body>
  ${html}
</body>
</html>
  `);

  printDocument.close();

  await new Promise(
    resolve => {
      requestAnimationFrame(
        () => {
          requestAnimationFrame(
            resolve
          );
        }
      );
    }
  );

  let cleaned = false;

  const cleanup = () => {
    if (cleaned) return;

    cleaned = true;

    window.setTimeout(
      () => frame.remove(),
      100
    );
  };

  printWindow.addEventListener(
    'afterprint',
    cleanup,
    { once: true }
  );

  const previousDocumentTitle =
    document.title;

  document.title =
    title;

  printWindow.focus();

  try {
    printWindow.print();
  } finally {
    /*
     * Keep the content-specific title long enough
     * for the browser print/save dialog to capture it,
     * then restore the application title.
     */
    window.setTimeout(
      () => {
        document.title =
          previousDocumentTitle;
      },
      1000
    );
  }

  window.setTimeout(
    cleanup,
    60000
  );
}


function getToday() {
  return getEffectiveToday();
}

function getCqWindowStatus(
  week,
  course
) {
  if (!week?.cq_enabled) {
    return 'disabled';
  }

  const match =
    String(
      week?.class_date || ''
    )
      .slice(0, 10)
      .match(
        /^(\d{4})-(\d{2})-(\d{2})$/
      );

  if (!match) {
    return 'closed';
  }

  const classDate =
    new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      0,
      0,
      0,
      0
    );

  const now =
    getEffectiveNow();

  const windowOpen =
    new Date(classDate);

  windowOpen.setDate(
    windowOpen.getDate() - 6
  );

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const classDay =
    dayNames[
      classDate.getDay()
    ];

  const resolvedTime =
    course?.class_times?.[
      classDay
    ] ||
    course?.class_time ||
    '09:00';

  const [
    hour,
    minute
  ] = String(
    resolvedTime
  )
    .slice(0, 5)
    .split(':')
    .map(Number);

  const classStart =
    new Date(classDate);

  classStart.setHours(
    Number.isFinite(hour)
      ? hour
      : 9,
    Number.isFinite(minute)
      ? minute
      : 0,
    0,
    0
  );

  const configuredHours =
    Number(
      course
        ?.cq_due_hours_before_class ??
      1
    );

  const hoursBeforeClass =
    Number.isFinite(
      configuredHours
    ) &&
    configuredHours >= 0
      ? configuredHours
      : 1;

  const windowClose =
    new Date(
      classStart.getTime() -
      hoursBeforeClass *
        60 *
        60 *
        1000
    );

  if (now < windowOpen) {
    return 'not_open';
  }

  if (now >= windowClose) {
    return 'closed';
  }

  return 'open';
}

function addDaysToDateString(dateStr, days) {
  if (!dateStr) return null;

  const match = String(dateStr)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0, 0, 0, 0
  );

  date.setDate(date.getDate() + Math.max(0, Number(days) || 0));

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function addSignedDaysToDateString(
  dateStr,
  days
) {
  if (
    !dateStr ||
    days === null ||
    days === undefined ||
    days === '' ||
    !Number.isFinite(Number(days))
  ) {
    return null;
  }

  const match = String(dateStr)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0, 0, 0, 0
  );

  date.setDate(
    date.getDate() + Number(days)
  );

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function getPdWindowStatus(week, course, submission = null) {
  const today = getToday();

  if (!week.pd_enabled) return 'disabled';
  if (week.class_date > today) return 'not_open';

  const dayNames = [
    'Sunday', 'Monday', 'Tuesday',
    'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  const targetDay = dayNames.indexOf(course.pd_due_day);
  if (targetDay < 0) return 'closed';

  const match = String(week.class_date)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return 'closed';

  const dueDate = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0, 0, 0, 0
  );

  while (dueDate.getDay() !== targetDay) {
    dueDate.setDate(dueDate.getDate() + 1);
  }

  dueDate.setDate(
    dueDate.getDate() +
    Math.max(0, Number(submission?.extension_days) || 0)
  );

  const dueDateStr = [
    dueDate.getFullYear(),
    String(dueDate.getMonth() + 1).padStart(2, '0'),
    String(dueDate.getDate()).padStart(2, '0')
  ].join('-');

  return today <= dueDateStr ? 'open' : 'closed';
}

function getSelfEvalStatus(course, section, extensionDays = null) {
  const today=getToday();

  const openDate=
    section==='Mid-Semester'
      ?course.mid_eval_open_date
      :course.final_eval_open_date;

  const closeDate=
    section==='Mid-Semester'
      ?course.mid_eval_close_date
      :course.final_eval_close_date;

  const configured=
    extensionDays==null
      ?(
          section==='Mid-Semester'
            ?course.mid_eval_extension_days
            :course.final_eval_extension_days
        )
      :extensionDays;

  const effective=
    addDaysToDateString(
      closeDate,
      Math.max(
        0,
        Number(configured)||0
      )
    );

  if(!openDate||!effective){
    return 'not_configured';
  }

  if(today<openDate){
    return 'not_open';
  }

  return today<=effective
    ?'open'
    :'closed';
}

function getAssignmentStatus(assignment, submission = null) {
  const today = getToday();

  const requiresSubmission =
    assignment?.requires_submission ??
    assignment?.assignment_types?.requires_submission ??
    true;

  if (!requiresSubmission) return 'no_submission';

  /*
   * An ordinary assignment needs a due date,
   * but does not need an explicit open date.
   */
  if (!assignment?.due_date) {
    return 'not_configured';
  }

  if (
    assignment?.open_date &&
    today < assignment.open_date
  ) {
    return 'not_open';
  }

  const effectiveDueDate = addDaysToDateString(
    assignment.due_date,
    submission?.extension_days || 0
  );

  if (!effectiveDueDate) return 'closed';
  return today <= effectiveDueDate ? 'open' : 'closed';
}

function latestFeedback(feedback) {
  if (!feedback) return null;
  const rows = Array.isArray(feedback) ? feedback : [feedback];
  if (rows.length === 0) return null;

  return [...rows].sort((a, b) => {
    const aTime = new Date(
      a.submitted_at || a.updated_at || a.created_at || 0
    ).getTime();
    const bTime = new Date(
      b.submitted_at || b.updated_at || b.created_at || 0
    ).getTime();

    return bTime - aTime;
  })[0] || null;
}

function instructorFeedbackTimeForGrid(
  feedback
) {
  return new Date(
    feedback?.submitted_at ||
    feedback?.updated_at ||
    feedback?.created_at ||
    0
  ).getTime();
}

function gradingGridCompletionStatus(
  instructorFeedbackRows,
  submissionType,
  submissionId,
  legacyFeedback
) {
  const firstSubmittedStatus =
    (instructorFeedbackRows || [])
      .filter(
        row =>
          row.submitted === true &&
          row.submission_type ===
            submissionType &&
          String(
            row.submission_id || ''
          ) ===
            String(
              submissionId || ''
            ) &&
          String(
            row.completion_status || ''
          ).trim()
      )
      .slice()
      .sort(
        (a, b) =>
          instructorFeedbackTimeForGrid(a) -
          instructorFeedbackTimeForGrid(b)
      )[0];

  return (
    firstSubmittedStatus
      ?.completion_status ||
    legacyFeedback
      ?.completion_status ||
    null
  );
}

function submissionHasUnpublishedGrade(
  rows,
  submissionId
) {
  if (!submissionId) return false;

  return (rows || []).some(
    row =>
      String(
        row.submission_id || ''
      ) ===
        String(submissionId) &&
      row.submitted === true &&
      row.is_unpublished === true
  );
}


function feedbackHasStudentResponse(
  feedback
) {
  return (
    String(
      feedback?.student_response || ''
    )
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .length > 0
  );
}

function assignmentSubmissionUploadProblem(
  submission
) {
  if (
    !submission?.submitted ||
    submission
      ?.structured_form_snapshot ||
    submission?.drive_file_id
  ) {
    return false;
  }

  const hasText =
    String(
      submission?.text || ''
    )
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .length > 0;

  return (
    !hasText &&
    !!String(
      submission?.file_name || ''
    ).trim()
  );
}


function CellStatus({
  status,
  submitted,
  graded,
  unpublished = false,
  completionStatus = null,
  studentResponded = false,
  uploadProblem = false,
  onClick,
  displayLabel = null,
  allowOpenWithoutSubmission = false
}) {
  let label;
  let style;

  const incomplete =
    submitted &&
    graded &&
    String(
      completionStatus || ''
    ).toLowerCase() ===
      'incomplete';

  if (uploadProblem) {
    label = 'Upload Error';
    style = styles.cellMissing;

  } else if (
    submitted &&
    unpublished
  ) {
    label = 'Graded, Unpublished';
    style = styles.cellUnpublished;

  } else if (incomplete) {
    label = 'Incomplete';
    style = styles.cellIncomplete;

  } else if (submitted && graded) {
    label = 'Graded';
    style = styles.cellGraded;

  } else if (submitted) {
    label = 'Submitted';
    style = styles.cellSubmitted;

  } else if (status === 'open') {
    label = 'Not Yet';
    style = styles.cellNotYet;

  } else if (status === 'closed') {
    label = 'Did Not Submit';
    style = styles.cellMissing;

  } else if (status === 'not_open') {
    label = 'Not Open';
    style = styles.cellNotOpen;

  } else if (status === 'not_chosen') {
    label = 'Not Chosen';
    style = styles.cellNotOpen;

  } else {
    label = '—';
    style = styles.cellDisabled;
  }

  const canOpen =
    !!onClick &&
    (
      submitted ||
      allowOpenWithoutSubmission
    );

  return (
    <button
      style={{
        ...styles.cell,
        ...style,
        ...(canOpen
          ? styles.cellClickable
          : {})
      }}
      onClick={
        canOpen
          ? onClick
          : undefined
      }
      disabled={!canOpen}
    >
      <span
        style={
          styles.cellContent
        }
      >
        <span>
          {displayLabel || label}
        </span>

        {studentResponded && (
          <span
            style={
              styles.studentReplyMarker
            }
            title="Student responded to feedback"
            aria-label="Student responded to feedback"
          >
            ↩
          </span>
        )}
      </span>
    </button>
  );
}

function getAssignmentGroupKey(assignment) {
  return String(
    assignment?.assignment_types?.id ||
    assignment?.assignment_type_id ||
    assignment?.assignment_types?.name ||
    'assignment'
  );
}

function getAssignmentTabKey(groupKey) {
  return `assignment-type:${groupKey}`;
}

function readStoredJson(key) {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readStoredString(key, fallback) {
  if (typeof window === 'undefined') return fallback;

  try {
    return window.sessionStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function readStudentSession() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem('studentSession');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function useTopHorizontalScrollbar(refreshKey) {
  const contentScrollRef =
    useRef(null);

  const topScrollRef =
    useRef(null);

  const [
    scrollMetrics,
    setScrollMetrics
  ] =
    useState({
      scrollWidth: 0,
      clientWidth: 0
    });

  const [
    floatingBar,
    setFloatingBar
  ] =
    useState({
      visible: false,
      left: 0,
      width: 0,
      bottom: 0
    });

  const updateFloatingBar =
    useCallback(() => {
      const scroller =
        contentScrollRef.current;

      if (
        !scroller ||
        typeof window === 'undefined'
      ) {
        setFloatingBar(
          previous =>
            previous.visible
              ? {
                  ...previous,
                  visible: false
                }
              : previous
        );

        return;
      }

      const rect =
        scroller
          .getBoundingClientRect();

      const viewportWidth =
        window.innerWidth ||
        document.documentElement
          .clientWidth ||
        0;

      const viewportHeight =
        window.innerHeight ||
        document.documentElement
          .clientHeight ||
        0;

      const hasOverflow =
        scroller.scrollWidth >
          scroller.clientWidth +
            1;

      const left =
        Math.max(
          0,
          Math.round(
            rect.left
          )
        );

      const right =
        Math.min(
          viewportWidth,
          Math.round(
            rect.right
          )
        );

      const width =
        Math.max(
          0,
          right - left
        );

      const visible =
        hasOverflow &&
        width > 0 &&
        rect.bottom > 0 &&
        rect.top <
          viewportHeight;

      /*
       * While the table extends below
       * the viewport, sit at the bottom
       * of the browser window.
       *
       * Once the real bottom of the
       * table comes into view, rise with
       * it instead of floating over the
       * content beneath the table.
       */
      const bottom =
        visible
          ? Math.max(
              0,
              Math.round(
                viewportHeight -
                  rect.bottom
              )
            )
          : 0;

      setFloatingBar(
        previous => {
          if (
            previous.visible ===
              visible &&
            previous.left ===
              left &&
            previous.width ===
              width &&
            previous.bottom ===
              bottom
          ) {
            return previous;
          }

          return {
            visible,
            left,
            width,
            bottom
          };
        }
      );

    }, []);

  const measure =
    useCallback(() => {
      const scroller =
        contentScrollRef.current;

      if (!scroller) {
        setScrollMetrics({
          scrollWidth: 0,
          clientWidth: 0
        });

        updateFloatingBar();
        return;
      }

      const next = {
        scrollWidth:
          Math.ceil(
            scroller.scrollWidth ||
            0
          ),

        clientWidth:
          Math.ceil(
            scroller.clientWidth ||
            0
          )
      };

      setScrollMetrics(
        previous =>
          previous.scrollWidth ===
              next.scrollWidth &&
          previous.clientWidth ===
              next.clientWidth
            ? previous
            : next
      );

      updateFloatingBar();

      if (
        topScrollRef.current
      ) {
        topScrollRef
          .current
          .scrollLeft =
            scroller.scrollLeft;
      }
    }, [
      updateFloatingBar
    ]);

  useEffect(() => {
    measure();

    const scroller =
      contentScrollRef.current;

    const content =
      scroller
        ?.firstElementChild ||
      null;

    const observer =
      typeof ResizeObserver !==
        'undefined'
        ? new ResizeObserver(
            measure
          )
        : null;

    if (scroller) {
      observer?.observe(
        scroller
      );
    }

    if (content) {
      observer?.observe(
        content
      );
    }

    window.addEventListener(
      'resize',
      measure
    );

    window.addEventListener(
      'scroll',
      updateFloatingBar,
      true
    );

    return () => {
      observer?.disconnect();

      window.removeEventListener(
        'resize',
        measure
      );

      window.removeEventListener(
        'scroll',
        updateFloatingBar,
        true
      );
    };
  }, [
    measure,
    refreshKey,
    updateFloatingBar
  ]);

  const onTopScroll =
    useCallback(
      event => {
        const scroller =
          contentScrollRef.current;

        if (!scroller) {
          return;
        }

        const next =
          event.currentTarget
            .scrollLeft;

        if (
          Math.abs(
            scroller.scrollLeft -
              next
          ) > 0.5
        ) {
          scroller.scrollLeft =
            next;
        }
      },
      []
    );

  const onContentScroll =
    useCallback(
      event => {
        const topScroller =
          topScrollRef.current;

        if (!topScroller) {
          return;
        }

        const next =
          event.currentTarget
            .scrollLeft;

        if (
          Math.abs(
            topScroller.scrollLeft -
              next
          ) > 0.5
        ) {
          topScroller.scrollLeft =
            next;
        }
      },
      []
    );

  return {
    contentScrollRef,
    topScrollRef,
    onTopScroll,
    onContentScroll,

    scrollWidth:
      scrollMetrics.scrollWidth,

    needsScroll:
      scrollMetrics.scrollWidth >
        scrollMetrics.clientWidth +
          1,

    floatingVisible:
      floatingBar.visible,

    floatingLeft:
      floatingBar.left,

    floatingWidth:
      floatingBar.width,

    floatingBottom:
      floatingBar.bottom
  };
}


async function saveClassExtensionValue({
  enrolmentId,
  assignmentId,
  days,
  instanceId = null,
  currentInstanceExtensions = {}
}) {
  const instanceExtensions = {
    ...(currentInstanceExtensions && typeof currentInstanceExtensions === 'object' && !Array.isArray(currentInstanceExtensions)
      ? currentInstanceExtensions
      : {})
  };

  if (instanceId) {
    if (days > 0) instanceExtensions[String(instanceId)] = days;
    else delete instanceExtensions[String(instanceId)];
  }

  const payload = instanceId
    ? { structured_form_instance_extensions: instanceExtensions }
    : { extension_days: days };

  const query =
    SUPABASE_URL +
    '/rest/v1/assignment_submissions?enrolment_id=eq.' + encodeURIComponent(enrolmentId) +
    '&course_assignment_id=eq.' + encodeURIComponent(assignmentId) +
    '&course_assignment_schedule_item_id=is.null&structured_form_instance_id=is.null';

  const request = async (url, method, body) => {
    const response = await fetch(url, {
      method,
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error('Failed to save extension (' + response.status + '): ' + await response.text());
    }

    return response.json();
  };

  const rows = await request(query, 'PATCH', payload);
  if (Array.isArray(rows) && rows.length) return rows;

  return request(
    SUPABASE_URL + '/rest/v1/assignment_submissions',
    'POST',
    {
      enrolment_id: enrolmentId,
      course_assignment_id: assignmentId,
      text: '',
      submitted: false,
      submitted_at: null,
      extension_days: instanceId ? 0 : days,
      ...(instanceId ? { structured_form_instance_extensions: instanceExtensions } : {})
    }
  );
}


async function savePdClassExtensionValue(
  enrolmentId,
  weekId,
  days
){
  const url=
    SUPABASE_URL+
    '/rest/v1/pd_submissions?enrolment_id=eq.'+
    encodeURIComponent(enrolmentId)+
    '&week_id=eq.'+
    encodeURIComponent(weekId);

  const headers={
    apikey:SUPABASE_KEY,
    Authorization:
      'Bearer '+SUPABASE_KEY,
    'Content-Type':
      'application/json',
    Prefer:
      'return=representation'
  };

  let r=
    await fetch(
      url,
      {
        method:'PATCH',
        headers,
        body:
          JSON.stringify({
            extension_days:days
          })
      }
    );

  if(!r.ok){
    throw new Error(
      'Failed to save PD extension ('+
      r.status+
      '): '+
      await r.text()
    );
  }

  let rows=
    await r.json();

  if(
    Array.isArray(rows)&&
    rows.length
  ){
    return rows;
  }

  r=
    await fetch(
      SUPABASE_URL+
        '/rest/v1/pd_submissions',
      {
        method:'POST',
        headers,

        body:
          JSON.stringify({
            enrolment_id:
              enrolmentId,

            week_id:
              weekId,

            text:
              '',

            submitted:
              false,

            submitted_at:
              null,

            extension_days:
              days
          })
      }
    );

  if(!r.ok){
    throw new Error(
      'Failed to create PD extension record ('+
      r.status+
      '): '+
      await r.text()
    );
  }

  return r.json();
}


async function saveSelfEvalClassExtensionValue(
  courseId,
  section,
  days
){
  const field=
    section==='Mid-Semester'
      ?'mid_eval_extension_days'
      :'final_eval_extension_days';

  const r=
    await fetch(
      SUPABASE_URL+
        '/rest/v1/courses?id=eq.'+
        encodeURIComponent(
          courseId
        ),
      {
        method:'PATCH',

        headers:{
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer '+
            SUPABASE_KEY,

          'Content-Type':
            'application/json',

          Prefer:
            'return=representation'
        },

        body:
          JSON.stringify({
            [field]:days
          })
      }
    );

  if(!r.ok){
    throw new Error(
      'Failed to save Self-Evaluation extension ('+
      r.status+
      '): '+
      await r.text()
    );
  }

  return r.json();
}

export default function AssignmentGrading({ course, session }) {
  const storageBase = `student-system:grading:${course.id}`;
  const gradingSession = session || readStudentSession();
  const canManageCourse =
    gradingSession?.isSuperadmin ||
    course.instructor_id === gradingSession?.instructorId;

  const [loading, setLoading] = useState(true);
  const [refreshError, setRefreshError] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [attendanceGroupMemberships, setAttendanceGroupMemberships] =
    useState([]);
  const [attendanceGroupWeeks, setAttendanceGroupWeeks] = useState([]);
  const [cqSubmissions, setCqSubmissions] = useState([]);

  const [
    cqManualSubmissionMarks,
    setCqManualSubmissionMarks
  ] = useState([]);

  const [
    cqQuestionViewer,
    setCqQuestionViewer
  ] = useState(null);

  const [
    cqReassigningId,
    setCqReassigningId
  ] = useState(null);

  const [classExtension, setClassExtension] = useState(null);

  const [
    builtInExtension,
    setBuiltInExtension
  ] =
    useState(null);

  const [
    selfEvalExtensionDays,
    setSelfEvalExtensionDays
  ] =
    useState({
      'Mid-Semester':
        Math.max(
          0,
          Number(
            course
              .mid_eval_extension_days
          )||0
        ),

      Final:
        Math.max(
          0,
          Number(
            course
              .final_eval_extension_days
          )||0
        )
    });


  useEffect(
    ()=>{
      setSelfEvalExtensionDays({
        'Mid-Semester':
          Math.max(
            0,
            Number(
              course
                .mid_eval_extension_days
            )||0
          ),

        Final:
          Math.max(
            0,
            Number(
              course
                .final_eval_extension_days
            )||0
          )
      });
    },
    [
      course.id,
      course
        .mid_eval_extension_days,
      course
        .final_eval_extension_days
    ]
  );


  useEffect(() => {
    if (
      (!cqQuestionViewer &&
        !classExtension &&
        !builtInExtension) ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [
    cqQuestionViewer,
    classExtension,
    builtInExtension
  ]);

  const [pdSubmissions, setPdSubmissions] = useState([]);

  const [
    instructorFeedbackRows,
    setInstructorFeedbackRows
  ] = useState([]);

  const [
    unpublishedGradingStatus,
    setUnpublishedGradingStatus
  ] = useState([]);

  const [
    publishingAssignmentId,
    setPublishingAssignmentId
  ] = useState(null);

  const [courseAssignments, setCourseAssignments] = useState([]);
  const [structuredFormInstances, setStructuredFormInstances] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [assignmentScheduleSettings, setAssignmentScheduleSettings] = useState([]);
  const [assignmentScheduleContext, setAssignmentScheduleContext] = useState({
    scheduleItems: [],
    scheduleReadings: [],
    scheduleEvents: [],
    choiceSelections: [],
    occupancy: []
  });
  const [assignmentScheduleSelections, setAssignmentScheduleSelections] = useState([]);
  const [assignmentGroupMemberships, setAssignmentGroupMemberships] = useState([]);
  const [
    structuredFormGroupWorkspaces,
    setStructuredFormGroupWorkspaces
  ] = useState([]);
  const [
    structuredFormGroupDeadlines,
    setStructuredFormGroupDeadlines
  ] = useState([]);
  const [
    structuredFormScheduleItems,
    setStructuredFormScheduleItems
  ] = useState([]);
  const [
    structuredFormScheduleSelections,
    setStructuredFormScheduleSelections
  ] = useState([]);
  const [assignmentCompletions, setAssignmentCompletions] = useState([]);
  const [selfEvalResponses, setSelfEvalResponses] = useState([]);
  const [qiAssignments, setQiAssignments] = useState([]);
  const [qiSubmissions, setQiSubmissions] = useState([]);
  const [assigningQi, setAssigningQi] = useState(false);
  const [attendancePrintWeekId, setAttendancePrintWeekId] =
    useState('all');

  const [
    attendancePrintStartMode,
    setAttendancePrintStartMode
  ] = useState('all');

  const [attendancePrinting, setAttendancePrinting] =
    useState(false);

  useEffect(() => {
    setAttendancePrintStartMode('all');
  }, [attendancePrintWeekId]);

  const [
    clarificatoryPrintWeekId,
    setClarificatoryPrintWeekId
  ] = useState('all');

  const [
    clarificatoryPrinting,
    setClarificatoryPrinting
  ] = useState(false);

  const [selectedType, setSelectedType] = useState(() =>
    readStoredString(`${storageBase}:tab`, 'Attendance')
  );

  const [gradingTarget, setGradingTarget] = useState(() =>
    readStoredJson(`${storageBase}:target`)
  );

  const refreshInFlight = useRef(false);
  const hasLoadedOnce = useRef(false);

  const refreshData = useCallback(async ({ showLoading = false } = {}) => {
    if (refreshInFlight.current) return null;

    refreshInFlight.current = true;

    if (showLoading && !hasLoadedOnce.current) {
      setLoading(true);
    }

    try {
      const [
        newWeeks,
        newEnrolments,
        newCourseAssignments,
        newInstructors,
        newStructuredFormInstances,
        newStructuredFormGroupWorkspaces
      ] = await Promise.all([
        fetchWeeks(course.id),
        fetchEnrolments(
          course.id,
          gradingSession?.isSuperadmin === true
        ),
        fetchCourseAssignments(course.id),
        fetchCourseInstructors(course.id),
        fetchStructuredFormInstances(course.id),
        fetchStructuredFormGroupWorkspaces(course.id)
      ]);

      const enrolmentIds = [
        ...new Set([
          ...newEnrolments.map(e => e.id),
          ...(newStructuredFormGroupWorkspaces || [])
            .map(row => row.owner_enrolment_id)
            .filter(Boolean)
        ])
      ].join(',');

      const structuredInstanceIds =
        newStructuredFormInstances.map(
          instance => instance.id
        );

      const [
        newStructuredFormGroupDeadlines,
        newStructuredFormScheduleItems,
        newStructuredFormScheduleSelections
      ] = await Promise.all([
        fetchStructuredFormGroupDeadlines(
          structuredInstanceIds
        ),
        fetchStructuredFormScheduleItems(
          structuredInstanceIds
        ),
        fetchStructuredFormScheduleSelections(
          structuredInstanceIds
        )
      ]);

      const attendanceGroupContext =
        course.group_based_attendance
          ? await fetchAttendanceGroupContext(course.id)
          : { memberships: [], groupWeeks: [] };

      const [
        newAttendance,
        newCq,
        newPd,
        newAssignmentSubmissions,
        newAssignmentScheduleSettings,
        newAssignmentScheduleContext,
        newAssignmentScheduleSelections,
        newAssignmentGroupMemberships,
        newAssignmentCompletions,
        newSelfEvals
      ] = await Promise.all([
        fetchAttendance(enrolmentIds),
        fetchCqSubmissions(enrolmentIds),
        fetchPdSubmissions(enrolmentIds),
        fetchAssignmentSubmissions(enrolmentIds),
        fetchAssignmentScheduleSettings(course.id),
        loadStudentAssignmentScheduleContext({
          courseIds: [course.id],
          enrolments: [],
          includeChoiceState: false
        }),
        fetchAssignmentScheduleSelections(course.id),
        fetchAssignmentGroupMembershipsForCourse(course.id),
        fetchAssignmentCompletions(enrolmentIds),
        fetchSelfEvalResponses(enrolmentIds)
      ]);

      const [
        newCqManualSubmissionMarks,
        newInstructorFeedbackRows,
        newUnpublishedGradingStatus
      ] = await Promise.all([
        fetchCqManualSubmissionMarks(
          enrolmentIds
        ),

        fetchInstructorFeedbackForGradingGrid(
          newPd,
          newAssignmentSubmissions
        ),

        fetchUnpublishedGradingStatus(
          course.id
        )
      ]);

      const assignmentDisplayNames =
  new Map(
    (
      newAssignmentScheduleSettings ||
      []
    ).map(
      setting => [
        String(
          setting.assignment_type_id
        ),
        String(
          setting.display_name || ''
        ).trim()
      ]
    )
  );

const gradingCourseAssignments =
  (
    newCourseAssignments ||
    []
  ).map(
    assignment => {
      const displayName =
        assignmentDisplayNames.get(
          String(
            assignment.assignment_type_id
          )
        );

      if (!displayName) {
        return assignment;
      }

      return {
        ...assignment,

        assignment_types: {
          ...assignment.assignment_types,
          name: displayName
        }
      };
    }
  );

      let newQiAssignments = [];
      let newQiSubmissions = [];

      if (course.qi_enabled) {
        const cqWeekIds = newWeeks
          .filter(week => week.cq_enabled)
          .map(week => week.id)
          .join(',');

        if (cqWeekIds) {
          const qi = await fetchQiData(cqWeekIds);
          newQiAssignments = qi.assignments;
          newQiSubmissions = qi.submissions;
        }
      }

      setWeeks(newWeeks);
      setEnrolments(newEnrolments);
      setInstructors(newInstructors);
      setAttendance(newAttendance);
      setAttendanceGroupMemberships(
        attendanceGroupContext.memberships
      );
      setAttendanceGroupWeeks(
        attendanceGroupContext.groupWeeks
      );
      setCqSubmissions(newCq);

      setCqManualSubmissionMarks(
        Array.isArray(
          newCqManualSubmissionMarks
        )
          ? newCqManualSubmissionMarks
          : []
      );

      setPdSubmissions(newPd);

      setInstructorFeedbackRows(
        Array.isArray(
          newInstructorFeedbackRows
        )
          ? newInstructorFeedbackRows
          : []
      );

      setUnpublishedGradingStatus(
        Array.isArray(
          newUnpublishedGradingStatus
        )
          ? newUnpublishedGradingStatus
          : []
      );

      setCourseAssignments(
  gradingCourseAssignments
);
      setStructuredFormInstances(newStructuredFormInstances);
      setAssignmentSubmissions(newAssignmentSubmissions);
      setAssignmentScheduleSettings(newAssignmentScheduleSettings);
      setAssignmentScheduleContext(newAssignmentScheduleContext);
      setAssignmentScheduleSelections(newAssignmentScheduleSelections);
      setAssignmentGroupMemberships(newAssignmentGroupMemberships);
      setStructuredFormGroupWorkspaces(
        Array.isArray(newStructuredFormGroupWorkspaces)
          ? newStructuredFormGroupWorkspaces
          : []
      );
      setStructuredFormGroupDeadlines(
        Array.isArray(newStructuredFormGroupDeadlines)
          ? newStructuredFormGroupDeadlines
          : []
      );
      setStructuredFormScheduleItems(
        Array.isArray(newStructuredFormScheduleItems)
          ? newStructuredFormScheduleItems
          : []
      );
      setStructuredFormScheduleSelections(
        Array.isArray(newStructuredFormScheduleSelections)
          ? newStructuredFormScheduleSelections
          : []
      );
      setAssignmentCompletions(newAssignmentCompletions);
      setSelfEvalResponses(newSelfEvals);
      setQiAssignments(newQiAssignments);
      setQiSubmissions(newQiSubmissions);
      setRefreshError('');

      hasLoadedOnce.current = true;
      return {
        weeks: newWeeks,
        enrolments: newEnrolments,
        attendance: newAttendance,
        cqSubmissions: newCq,
        pdSubmissions: newPd,
        courseAssignments:
  gradingCourseAssignments,
        structuredFormInstances: newStructuredFormInstances,
        assignmentSubmissions: newAssignmentSubmissions,
        assignmentCompletions: newAssignmentCompletions,
        selfEvalResponses: newSelfEvals,
        qiAssignments: newQiAssignments,
        qiSubmissions: newQiSubmissions
      };
    } catch (err) {
      console.error('Grading refresh failed:', err);
      setRefreshError('Could not refresh grading data.');
      return null;
    } finally {
      refreshInFlight.current = false;
      setLoading(false);
    }
  }, [
  course.id,
  course.qi_enabled,
  course.group_based_attendance,
  gradingSession?.isSuperadmin
]);

  useEffect(() => {
    hasLoadedOnce.current = false;
    refreshData({ showLoading: true });
  }, [refreshData]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      gradingTarget
    ) {
      return undefined;
    }

    /*
     * Do not poll the entire grading dataset.
     * Refresh only when the instructor actually
     * returns to the page.
     */
    const handleFocus =
      () => refreshData();

    const handleVisibility =
      () => {
        if (
          document.visibilityState ===
          'visible'
        ) {
          refreshData();
        }
      };

    window.addEventListener(
      'focus',
      handleFocus
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      window.removeEventListener(
        'focus',
        handleFocus
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [refreshData, gradingTarget]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      window.sessionStorage.setItem(
        `${storageBase}:tab`,
        selectedType
      );
    } catch {
      // Navigation persistence is optional.
    }
  }, [storageBase, selectedType]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (gradingTarget) {
        window.sessionStorage.setItem(
          `${storageBase}:target`,
          JSON.stringify(gradingTarget)
        );
      } else {
        window.sessionStorage.removeItem(`${storageBase}:target`);
      }
    } catch {
      // Navigation persistence is optional.
    }
  }, [storageBase, gradingTarget]);

  const assignmentGroups = useMemo(() => {
    const groups = Object.values(
      courseAssignments.reduce((result, assignment, assignmentIndex) => {
        const groupKey = getAssignmentGroupKey(assignment);

        if (!result[groupKey]) {
          result[groupKey] = {
            key: groupKey,
            name: assignment.assignment_types?.name || 'Assignment',
            assignments: [],
            originalIndex: assignmentIndex
          };
        }

        result[groupKey].assignments.push(assignment);
        return result;
      }, {})
    );

    return groups
      .map(group => ({
        ...group,
        assignments: [...group.assignments].sort(
          (a, b) =>
            (Number(a.instance_number) || 0) -
              (Number(b.instance_number) || 0) ||
            String(a.id).localeCompare(String(b.id))
        )
      }))
      .sort((a, b) => {
        const aSetting = assignmentScheduleSettings.find(
          setting =>
            String(setting.assignment_type_id) === String(a.key)
        );
        const bSetting = assignmentScheduleSettings.find(
          setting =>
            String(setting.assignment_type_id) === String(b.key)
        );

        const aRawOrder = aSetting?.menu_order;
        const bRawOrder = bSetting?.menu_order;

        const aOrder =
          aRawOrder === null ||
          aRawOrder === undefined ||
          aRawOrder === ''
            ? null
            : Number(aRawOrder);

        const bOrder =
          bRawOrder === null ||
          bRawOrder === undefined ||
          bRawOrder === ''
            ? null
            : Number(bRawOrder);

        const aHasOrder =
          aOrder !== null &&
          Number.isFinite(aOrder);
        const bHasOrder =
          bOrder !== null &&
          Number.isFinite(bOrder);

        if (aHasOrder && bHasOrder) {
          return aOrder - bOrder || a.originalIndex - b.originalIndex;
        }
        if (aHasOrder) return -1;
        if (bHasOrder) return 1;
        return a.originalIndex - b.originalIndex;
      });
  }, [courseAssignments, assignmentScheduleSettings]);

  const activeAssignmentGroup = assignmentGroups.find(
    group => getAssignmentTabKey(group.key) === selectedType
  );

  useEffect(() => {
    if (
      selectedType === 'Self-Evaluation (Mid)' ||
      selectedType === 'Self-Evaluation (Final)'
    ) {
      setSelectedType('Self-Evaluation');
    }
  }, [selectedType]);

  useEffect(() => {
    if (getBuiltInTypes(course).includes(selectedType)) return;
    if (activeAssignmentGroup) return;

    // Upgrade an old saved per-instance tab to its new grouped type tab.
    const legacyAssignment = courseAssignments.find(
      assignment => assignment.id === selectedType
    );

    if (legacyAssignment) {
      setSelectedType(
        getAssignmentTabKey(getAssignmentGroupKey(legacyAssignment))
      );
      return;
    }

    const validGroupedTab = assignmentGroups.some(
      group => getAssignmentTabKey(group.key) === selectedType
    );

    if (hasLoadedOnce.current && !validGroupedTab) {
      const builtIns = getBuiltInTypes(course);
      setSelectedType(builtIns[0] || (assignmentGroups[0] ? getAssignmentTabKey(assignmentGroups[0].key) : ''));
    }
  }, [
    course,
    selectedType,
    activeAssignmentGroup,
    assignmentGroups,
    courseAssignments
  ]);

  async function handleTabChange(type) {
    setSelectedType(type);
    setGradingTarget(null);
    await refreshData();
  }

  async function handleToggleAttendance(
    enrolmentId,
    weekId,
    currentPresent
  ) {
    const nextPresent = !currentPresent;
    setRefreshError('');

    try {
      const result = await saveAttendance(
        enrolmentId,
        weekId,
        nextPresent
      );

      const savedRow = result?.[0] || {
        enrolment_id: enrolmentId,
        week_id: weekId,
        present: nextPresent
      };

      setAttendance(prev => {
        const existing = prev.find(
          record =>
            record.enrolment_id === enrolmentId &&
            record.week_id === weekId
        );

        if (existing) {
          return prev.map(record =>
            record.enrolment_id === enrolmentId &&
            record.week_id === weekId
              ? {
                  ...record,
                  ...savedRow,
                  present: nextPresent
                }
              : record
          );
        }

        return [
          ...prev,
          {
            ...savedRow,
            enrolment_id: enrolmentId,
            week_id: weekId,
            present: nextPresent
          }
        ];
      });

      await refreshData();
    } catch (err) {
      console.error('Attendance save failed:', err);
      setRefreshError('Could not save attendance.');
    }
  }

  async function handleToggleAssignmentCompletion(
    enrolmentId,
    courseAssignmentId,
    currentCompleted
  ) {
    const nextCompleted = !currentCompleted;
    setRefreshError('');

    try {
      const result = await saveAssignmentCompletion(
        enrolmentId,
        courseAssignmentId,
        nextCompleted
      );

      const updatedRow = result?.[0] || {
        enrolment_id: enrolmentId,
        course_assignment_id: courseAssignmentId,
        completed: nextCompleted
      };

      setAssignmentCompletions(prev => {
        const existing = prev.find(row =>
          row.enrolment_id === enrolmentId &&
          row.course_assignment_id === courseAssignmentId
        );

        if (existing) {
          return prev.map(row =>
            row.enrolment_id === enrolmentId &&
            row.course_assignment_id === courseAssignmentId
              ? { ...row, ...updatedRow, completed: nextCompleted }
              : row
          );
        }

        return [...prev, updatedRow];
      });

      refreshData();
    } catch (err) {
      console.error('Assignment completion save failed:', err);
      setRefreshError(
        err?.message || 'Could not save assignment completion.'
      );
    }
  }

  async function handleAssignQi(weekId) {
    setAssigningQi(true);
    setRefreshError('');

    try {
      const updated =
        await assignQiQuestionsForWeek({
          weekId,
          cqSubmissions,
          enrolments,
          allowReassign: false
        });

      setQiAssignments(
        previous => [
          ...previous.filter(
            assignment =>
              String(
                assignment
                  .course_week_id
              ) !==
              String(
                weekId
              )
          ),

          ...updated
        ]
      );

      refreshData();

    } catch (err) {
      console.error(
        'QI assignment error:',
        err
      );

      setRefreshError(
        err?.message ||
        'Could not assign QI questions.'
      );

    } finally {
      setAssigningQi(false);
    }
  }

  async function handleManualCqSubmissionChange(
    enrolmentId,
    weekId,
    checked
  ) {
    setRefreshError('');

    try {
      if (checked) {
        const result =
          await saveCqManualSubmissionMark(
            enrolmentId,
            weekId,
            gradingSession
              ?.instructorId ||
              null
          );

        const saved =
          result?.[0] || {
            enrolment_id:
              enrolmentId,

            week_id:
              weekId,

            marked_at:
              new Date()
                .toISOString(),

            marked_by:
              gradingSession
                ?.instructorId ||
              null
          };

        setCqManualSubmissionMarks(
          previous => [
            ...previous.filter(
              row =>
                !(
                  String(
                    row.enrolment_id
                  ) ===
                    String(
                      enrolmentId
                    ) &&
                  String(
                    row.week_id
                  ) ===
                    String(
                      weekId
                    )
                )
            ),

            saved
          ]
        );

        return;
      }

      await deleteCqManualSubmissionMark(
        enrolmentId,
        weekId
      );

      setCqManualSubmissionMarks(
        previous =>
          previous.filter(
            row =>
              !(
                String(
                  row.enrolment_id
                ) ===
                  String(
                    enrolmentId
                  ) &&
                String(
                  row.week_id
                ) ===
                  String(
                    weekId
                  )
              )
          )
      );

    } catch (err) {
      console.error(
        'Manual CQ submission update failed:',
        err
      );

      setRefreshError(
        err?.message ||
        'Could not update the manual Critical Question submission.'
      );
    }
  }


  async function handleViewerCqReassign(
    submission
  ) {
    if (
      !submission?.id ||
      cqReassigningId
    ) {
      return;
    }

    const currentType =
      submission.question_type ===
        'Clarificatory'
        ? 'Clarificatory'
        : 'Critical';

    const nextType =
      currentType ===
        'Clarificatory'
        ? 'Critical'
        : 'Clarificatory';

    setCqReassigningId(
      submission.id
    );

    setRefreshError('');

    try {
      if (
        currentType ===
          'Critical' &&
        nextType ===
          'Clarificatory'
      ) {
        const assignmentResponse =
          await fetch(
            SUPABASE_URL +
              '/rest/v1/cq_question_assignments?cq_submission_id=eq.' +
              encodeURIComponent(
                submission.id
              ) +
              '&select=id&limit=1',
            {
              headers:
                apiHeaders()
            }
          );

        const assignmentRows =
          await readJson(
            assignmentResponse,
            'Checking Question Improvement assignment'
          );

        if (
          Array.isArray(
            assignmentRows
          ) &&
          assignmentRows.length > 0
        ) {
          throw new Error(
            'This Critical Question has already been assigned for Question Improvement and cannot be changed to Clarificatory.'
          );
        }
      }

      const response =
        await fetch(
          SUPABASE_URL +
            '/rest/v1/cq_submissions?id=eq.' +
            encodeURIComponent(
              submission.id
            ),
          {
            method: 'PATCH',

            headers: {
              ...apiHeaders(),

              'Content-Type':
                'application/json',

              'Prefer':
                'return=representation'
            },

            body:
              JSON.stringify({
                question_type:
                  nextType
              })
          }
        );

      const rows =
        await readJson(
          response,
          'Reassigning question'
        );

      const updated =
        Array.isArray(rows) &&
        rows[0]
          ? rows[0]
          : {
              ...submission,
              question_type:
                nextType
            };

      setCqSubmissions(
        previous =>
          previous.map(
            row =>
              String(row.id) ===
                String(
                  submission.id
                )
                ? {
                    ...row,
                    ...updated
                  }
                : row
          )
      );

      setCqQuestionViewer(
        previous =>
          previous &&
          String(
            previous
              .submission
              ?.id
          ) ===
            String(
              submission.id
            )
            ? {
                ...previous,

                submission: {
                  ...previous
                    .submission,
                  ...updated
                }
              }
            : previous
      );

    } catch (err) {
      setRefreshError(
        err?.message ||
        'Could not reassign question.'
      );

    } finally {
      setCqReassigningId(
        null
      );
    }
  }


  function openGradingTarget(target) {
    if(
      typeof document!=='undefined' &&
      document.activeElement &&
      typeof document
        .activeElement
        .blur==='function'
    ){
      document
        .activeElement
        .blur();
    }

    setGradingTarget(target);
    refreshData();
  }

  function getQiStatus(
    enrolment,
    weekId
  ) {
    if (!course.qi_enabled) {
      return null;
    }

    const weekAssignments =
      qiAssignments.filter(
        assignment =>
          assignment
            .course_week_id ===
          weekId
      );

    if (!weekAssignments.length) {
      return 'not_assigned';
    }

    const assignmentIds =
      new Set(
        weekAssignments.map(
          assignment =>
            String(assignment.id)
        )
      );

    const targetId =
      String(enrolment.id);

    const participantWork =
      qiSubmissions.filter(
        submission => {
          if (
            !assignmentIds.has(
              String(
                submission
                  .cq_question_assignment_id
              )
            )
          ) {
            return false;
          }

          if (
            String(
              submission.enrolment_id ||
              ''
            ) === targetId
          ) {
            return true;
          }

          return (
            Array.isArray(
              submission
                .group_member_ids
            ) &&
            submission
              .group_member_ids
              .some(
                id =>
                  String(id) ===
                  targetId
              )
          );
        }
      );

    if (
      participantWork.some(
        submission =>
          submission.submitted ===
          true
      )
    ) {
      return 'submitted';
    }

    const assignedGroup =
      weekAssignments.filter(
        assignment =>
          Number(
            assignment.stream
          ) ===
            Number(
              enrolment.stream
            ) &&
          Number(
            assignment.group_number
          ) ===
            Number(
              enrolment.group_number
            )
      );

    if (!assignedGroup.length) {
      return participantWork.length
        ? 'draft'
        : 'not_assigned';
    }

    const assignedIds =
      new Set(
        assignedGroup.map(
          assignment =>
            String(assignment.id)
        )
      );

    const groupSubmitted =
      qiSubmissions.some(
        submission =>
          assignedIds.has(
            String(
              submission
                .cq_question_assignment_id
            )
          ) &&
          submission.submitted ===
            true
      );

    if (groupSubmitted) {
      return 'not_in_submission';
    }

    return participantWork.length
      ? 'draft'
      : 'assigned';
  }

  function isAttendanceRequired(enrolmentId, weekId) {
    if (!course.group_based_attendance) {
      return weeks.some(
        week =>
          week.id === weekId &&
          week.attendance_enabled
      );
    }

    const membership = attendanceGroupMemberships.find(
      row => row.enrolment_id === enrolmentId
    );

    if (!membership) return false;

    return attendanceGroupWeeks.some(
      row =>
        row.attendance_group_id === membership.attendance_group_id &&
        row.course_week_id === weekId
    );
  }

  function getPrintableAttendanceWeeks() {
    const scheduledWeekIds =
      course.group_based_attendance
        ? new Set(
            attendanceGroupWeeks.map(
              row => row.course_week_id
            )
          )
        : null;

    return weeks.filter(week =>
      course.group_based_attendance
        ? scheduledWeekIds.has(week.id)
        : week.attendance_enabled
    );
  }

    function normaliseAttendanceStartTime(
    value
  ) {
    if (!value) return '';
  
    return String(value)
      .trim()
      .slice(0, 5);
  }

  function getAttendanceBaseStartTime(
    week
  ) {
    if (!week?.class_date) {
      return '';
    }
  
    const match =
      String(week.class_date)
        .slice(0, 10)
        .match(
          /^(\d{4})-(\d{2})-(\d{2})$/
        );
  
    if (!match) {
      return '';
    }
  
    const date =
      new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        12,
        0,
        0,
        0
      );
  
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];
  
    const day =
      dayNames[
        date.getDay()
      ];
  
    return normaliseAttendanceStartTime(
      course?.class_times?.[day] ||
      course?.class_time ||
      ''
    );
  }

  function getAttendanceGroupWeek(
    enrolmentId,
    weekId
  ) {
    const membership =
      attendanceGroupMemberships.find(
        row =>
          String(row.enrolment_id) ===
          String(enrolmentId)
      );
  
    if (!membership) {
      return null;
    }
  
    return (
      attendanceGroupWeeks.find(
        row =>
          String(
            row.attendance_group_id
          ) ===
            String(
              membership.attendance_group_id
            ) &&
          String(
            row.course_week_id
          ) ===
            String(weekId)
      ) ||
      null
    );
  }

function getAttendanceEffectiveStartTime(
  enrolment,
  week
) {
  const normal =
    getAttendanceBaseStartTime(
      week
    );

  if (
    course?.group_based_attendance ===
    true
  ) {
    const groupWeek =
      getAttendanceGroupWeek(
        enrolment.id,
        week.id
      );

    return (
      normaliseAttendanceStartTime(
        groupWeek
          ?.start_time_override
      ) ||
      normal
    );
  }

  return (
    normaliseAttendanceStartTime(
      week?.start_time_override
    ) ||
    normal
  );
}

  function getSelectedAttendancePrintWeeks() {
    const printableWeeks =
      getPrintableAttendanceWeeks();
  
    return attendancePrintWeekId ===
      'all'
      ? printableWeeks
      : printableWeeks.filter(
          week =>
            String(week.id) ===
            String(
              attendancePrintWeekId
            )
        );
  }

function selectedAttendanceHasTimeOverride() {
  const selectedWeekIds =
    new Set(
      getSelectedAttendancePrintWeeks()
        .map(
          week =>
            String(week.id)
        )
    );

  if (
    selectedWeekIds.size === 0
  ) {
    return false;
  }

  if (
    course?.group_based_attendance ===
    true
  ) {
    return attendanceGroupWeeks.some(
      row =>
        selectedWeekIds.has(
          String(
            row.course_week_id
          )
        ) &&
        !!normaliseAttendanceStartTime(
          row.start_time_override
        )
    );
  }

  return getSelectedAttendancePrintWeeks()
    .some(
      week =>
        !!normaliseAttendanceStartTime(
          week.start_time_override
        )
    );
}

  function getAttendanceStartTimeOptions() {
    const times =
      new Set();
  
    getSelectedAttendancePrintWeeks()
      .forEach(
        week => {
          enrolments
            .filter(
              enrolment =>
                isAttendanceRequired(
                  enrolment.id,
                  week.id
                )
            )
            .forEach(
              enrolment => {
                const time =
                  getAttendanceEffectiveStartTime(
                    enrolment,
                    week
                  );
  
                if (time) {
                  times.add(time);
                }
              }
            );
        }
      );
  
    return [...times].sort(
      (a, b) =>
        a.localeCompare(b)
    );
  }

  function compareAttendanceStudentIds(
    a,
    b
  ) {
    return String(
      a?.students?.student_code ||
      ''
    ).localeCompare(
      String(
        b?.students?.student_code ||
        ''
      ),
      undefined,
      {
        numeric: true,
        sensitivity: 'base'
      }
    );
  }

  function getAttendanceSheetStudents(
    weekId,
    startMode = 'all'
  ) {
    const week =
      weeks.find(
        item =>
          String(item.id) ===
          String(weekId)
      );
  
    if (!week) {
      return [];
    }
  
    let students =
      enrolments.filter(
        enrolment =>
          isAttendanceRequired(
            enrolment.id,
            weekId
          )
      );
  
    if (
      startMode.startsWith(
        'time:'
      )
    ) {
      const wantedTime =
        startMode.slice(
          'time:'.length
        );
  
      students =
        students.filter(
          enrolment =>
            getAttendanceEffectiveStartTime(
              enrolment,
              week
            ) ===
            wantedTime
        );
    }
  
    return students
      .slice()
      .sort(
        (a, b) => {
          if (
            startMode ===
            'separate'
          ) {
            const aTime =
              getAttendanceEffectiveStartTime(
                a,
                week
              );
  
            const bTime =
              getAttendanceEffectiveStartTime(
                b,
                week
              );
  
            const timeCompare =
              String(aTime)
                .localeCompare(
                  String(bTime)
                );
  
            if (
              timeCompare !== 0
            ) {
              return timeCompare;
            }
          }
  
          return compareAttendanceStudentIds(
            a,
            b
          );
        }
      );
  }

  function renderAttendanceStudentRow(
    enrolment
  ) {
    const student =
      enrolment.students ||
      {};
  
    return `
      <tr>
        <td>${escapeAttendanceHtml(
          student.student_code ||
          ''
        )}</td>
  
        <td>${escapeAttendanceHtml(
          student.chinese_name ||
          ''
        )}</td>
  
        <td>${escapeAttendanceHtml(
          student.english_name ||
          ''
        )}</td>
  
        <td></td>
      </tr>
    `;
  }

  function renderAttendanceRows(
    students,
    week,
    startMode
  ) {
    if (
      students.length === 0
    ) {
      return `
        <tr>
          <td
            colspan="4"
            style="
              text-align:center;
              color:#666;
            "
          >
            No students match this attendance sheet.
          </td>
        </tr>
      `;
    }
  
    if (
      startMode.startsWith(
        'time:'
      )
    ) {
      const time =
        startMode.slice(
          'time:'.length
        );
  
      return `
        <tr>
          <td
            colspan="4"
            style="
              background:#e9eef2;
              font-weight:700;
            "
          >
            Start time:
            ${escapeAttendanceHtml(
              time
            )}
          </td>
        </tr>
  
        ${students
          .map(
            renderAttendanceStudentRow
          )
          .join('')}
      `;
    }
  
    if (
      startMode !==
      'separate'
    ) {
      return students
        .map(
          renderAttendanceStudentRow
        )
        .join('');
    }
  
    const byTime =
      new Map();
  
    students.forEach(
      enrolment => {
        const time =
          getAttendanceEffectiveStartTime(
            enrolment,
            week
          ) ||
          'Time not set';
  
        if (
          !byTime.has(time)
        ) {
          byTime.set(
            time,
            []
          );
        }
  
        byTime
          .get(time)
          .push(enrolment);
      }
    );
  
    return [...byTime.entries()]
      .sort(
        ([a], [b]) =>
          String(a)
            .localeCompare(
              String(b)
            )
      )
      .map(
        ([
          time,
          groupStudents
        ]) => `
          <tr>
            <td
              colspan="4"
              style="
                background:#e9eef2;
                font-weight:700;
              "
            >
              Start time:
              ${escapeAttendanceHtml(
                time
              )}
            </td>
          </tr>
  
          ${groupStudents
            .slice()
            .sort(
              compareAttendanceStudentIds
            )
            .map(
              renderAttendanceStudentRow
            )
            .join('')}
        `
      )
      .join('');
  }

  async function handlePrintAttendanceSheet() {
    const printableWeeks =
      getPrintableAttendanceWeeks();

    const selectedWeeks =
      attendancePrintWeekId === 'all'
        ? printableWeeks
        : printableWeeks.filter(
            week =>
              String(week.id) ===
              String(attendancePrintWeekId)
          );

    if (selectedWeeks.length === 0) {
      setRefreshError(
        'No attendance weeks are available to print.'
      );
      return;
    }

    const availableStartTimes =
      getAttendanceStartTimeOptions();

    let effectiveStartMode =
      selectedAttendanceHasTimeOverride()
        ? attendancePrintStartMode
        : 'all';

    if (
      effectiveStartMode.startsWith(
        'time:'
      ) &&
      !availableStartTimes.includes(
        effectiveStartMode.slice(
          'time:'.length
        )
      )
    ) {
      effectiveStartMode =
        'all';
    }

    setAttendancePrinting(true);
    setRefreshError('');

    try {
      const courseTitle =
        course.course_titles?.name ||
        course.name ||
        course.course_name ||
        course.title ||
        'Attendance';

      const pages = selectedWeeks.map(
        (week, index) => {
          const students =
            getAttendanceSheetStudents(
              week.id,
              effectiveStartMode
            );

          const rows =
            renderAttendanceRows(
              students,
              week,
              effectiveStartMode
            );

          const pageBreak =
            index < selectedWeeks.length - 1
              ? 'break-after: page; page-break-after: always;'
              : '';

          return `
            <section style="${pageBreak}">
              <div style="
                font-family: Arial, 'Noto Sans TC',
                  'Microsoft JhengHei', sans-serif;
                color: #111;
              ">
                <h1>
                  ${escapeAttendanceHtml(courseTitle)}
                </h1>

                <div style="
                  margin: 0 0 14px;
                  font-size: 14px;
                  font-weight: 700;
                  break-after: avoid-page;
                  page-break-after: avoid;
                ">
                  ${escapeAttendanceHtml(
                    formatAttendanceSheetWeek(week)
                  )}
                </div>

                <table>
                  <thead>
                    <tr>
                      <th style="width:18%;">ID</th>
                      <th style="width:20%;">Chinese Name</th>
                      <th style="width:27%;">English Name</th>
                      <th style="width:35%;">Signature</th>
                    </tr>
                  </thead>

                  <tbody>
                    ${rows}
                  </tbody>
                </table>
              </div>
            </section>
          `;
        }
      ).join('');

      const suffix =
        attendancePrintWeekId === 'all'
          ? 'All Weeks'
          : formatAttendanceSheetWeek(
              selectedWeeks[0]
            );

      const fileName =
        `${safeAttendanceFilePart(
          courseTitle
        )} - Attendance - ${safeAttendanceFilePart(
          suffix
        )}.pdf`;

      await printAttendanceSheetHtml(
        pages,
        fileName
      );
    } catch (err) {
      console.error(
        'Attendance sheet print failed:',
        err
      );

      setRefreshError(
        err?.message ||
        'Could not print the attendance sheet.'
      );
    } finally {
      setAttendancePrinting(false);
    }
  }


  function getPrintableClarificatoryWeeks() {
    return weeks.filter(
      week =>
        week.cq_enabled === true
    );
  }


  function getClarificatoryQuestionsForWeek(
    weekId
  ) {
    return cqSubmissions
      .filter(
        submission =>
          String(
            submission.week_id
          ) ===
            String(weekId) &&
          submission.question_type ===
            'Clarificatory' &&
          String(
            submission.question_text ||
            ''
          ).trim()
      )
      .slice()
      .sort(
        (a, b) =>
          String(
            a.submitted_at || ''
          ).localeCompare(
            String(
              b.submitted_at || ''
            )
          )
      );
  }


  function getClarificatoryFontSize(
    question
  ) {
    const length =
      String(
        question || ''
      ).trim().length;

    if (length <= 140) {
      return '38pt';
    }

    if (length <= 240) {
      return '33pt';
    }

    if (length <= 380) {
      return '28pt';
    }

    if (length <= 550) {
      return '24pt';
    }

    return '21pt';
  }


  async function handlePrintClarificatoryQuestions() {
    const printableWeeks =
      getPrintableClarificatoryWeeks();

    const selectedWeeks =
      clarificatoryPrintWeekId ===
        'all'
        ? printableWeeks
        : printableWeeks.filter(
            week =>
              String(week.id) ===
              String(
                clarificatoryPrintWeekId
              )
          );

    if (
      selectedWeeks.length === 0
    ) {
      setRefreshError(
        'No Critical Question weeks are available.'
      );

      return;
    }

    const questionRows =
      selectedWeeks.flatMap(
        week =>
          getClarificatoryQuestionsForWeek(
            week.id
          ).map(
            submission => ({
              week,
              submission
            })
          )
      );

    if (
      questionRows.length === 0
    ) {
      setRefreshError(
        'No Clarificatory Questions have been submitted for the selected week.'
      );

      return;
    }

    setClarificatoryPrinting(
      true
    );

    setRefreshError('');

    try {
      const courseTitle =
        course.course_titles
          ?.name ||
        course.name ||
        course.course_name ||
        course.title ||
        'Course';

      const total =
        questionRows.length;

      const pages =
        questionRows
          .map(
            (
              {
                week,
                submission
              },
              index
            ) => {
              const question =
                String(
                  submission
                    .question_text ||
                  ''
                ).trim();

              const escapedQuestion =
                escapeAttendanceHtml(
                  question
                ).replace(
                  /\n/g,
                  '<br />'
                );

              const fontSize =
                getClarificatoryFontSize(
                  question
                );

              return `
                <section class="question-page">

                  <div class="question-header">
                    <span>
                      ${escapeAttendanceHtml(
                        courseTitle
                      )}
                    </span>

                    <span>
                      ${escapeAttendanceHtml(
                        formatAttendanceSheetWeek(
                          week
                        )
                      )}
                    </span>
                  </div>

                  <div class="question-body">
                    <div
                      class="question-text"
                      style="
                        font-size:
                          ${fontSize};
                      "
                    >
                      ${escapedQuestion}
                    </div>
                  </div>

                  <div class="question-footer">
                    Clarificatory Question
                    ${index + 1}
                    of
                    ${total}
                  </div>

                </section>
              `;
            }
          )
          .join('');

      const suffix =
        clarificatoryPrintWeekId ===
          'all'
          ? 'All Weeks'
          : formatAttendanceSheetWeek(
              selectedWeeks[0]
            );

      const fileName =
        `${safeAttendanceFilePart(
          courseTitle
        )} - Clarificatory Questions - ${safeAttendanceFilePart(
          suffix
        )}.pdf`;

      await printClarificatoryQuestionsHtml(
        pages,
        fileName
      );

    } catch (err) {
      console.error(
        'Clarificatory Questions print failed:',
        err
      );

      setRefreshError(
        err?.message ||
        'Could not print the Clarificatory Questions.'
      );

    } finally {
      setClarificatoryPrinting(
        false
      );
    }
  }


   function getScheduleSetting(assignment) {
    return assignmentScheduleSettings.find(
      setting =>
        String(setting.assignment_type_id) ===
        String(assignment.assignment_type_id)
    ) || null;
  }

  function getStructuredFormGroupContext(
    enrolmentId,
    assignment
  ) {
    const usesGroups =
      assignment?.groups_enabled === true &&
      Number(assignment?.group_count) > 0;

    if (!usesGroups) {
      return {
        groupNumber: null,
        ownerEnrolmentId: enrolmentId,
        workspace: null
      };
    }

    const membership =
      assignmentGroupMemberships.find(
        row =>
          String(row.course_assignment_id) ===
            String(assignment.id) &&
          String(row.enrolment_id) ===
            String(enrolmentId)
      ) || null;

    if (!membership) {
      return {
        groupNumber: null,
        ownerEnrolmentId: enrolmentId,
        workspace: null
      };
    }

    const workspace =
      structuredFormGroupWorkspaces.find(
        row =>
          String(row.course_assignment_id) ===
            String(assignment.id) &&
          Number(row.group_number) ===
            Number(membership.group_number)
      ) || null;

    return {
      groupNumber: Number(membership.group_number),
      ownerEnrolmentId:
        workspace?.owner_enrolment_id ||
        enrolmentId,
      workspace
    };
  }

  function getStructuredFormGroupDeadline(
    instanceId,
    groupNumber
  ) {
    if (!instanceId || !groupNumber) {
      return null;
    }

    return structuredFormGroupDeadlines.find(
      row =>
        String(
          row.course_structured_instance_id
        ) ===
          String(instanceId) &&
        Number(row.group_number) ===
          Number(groupNumber)
    ) || null;
  }

  function getStructuredFormScheduleSource(
    item
  ) {
    if (!item) {
      return {
        label: '',
        anchorDate: null
      };
    }

    if (item.item_type === 'reading') {
      const occurrence =
        (assignmentScheduleContext
          .scheduleReadings || [])
          .find(
            row =>
              String(row.id) ===
              String(
                item.week_reading_assignment_id
              )
          );

      const week =
        occurrence?.course_weeks;

      const reading =
        occurrence?.course_readings;

      return {
        label: [
          week?.week_number
            ? `Week ${week.week_number}`
            : null,
          reading?.title || 'Reading',
          reading?.author
            ? `by ${reading.author}`
            : null
        ].filter(Boolean).join(' · '),
        anchorDate:
          week?.class_date || null
      };
    }

    const event =
      (assignmentScheduleContext
        .scheduleEvents || [])
        .find(
          row =>
            String(row.id) ===
            String(item.course_event_id)
        );

    const week =
      event?.course_weeks;

    return {
      label: [
        week?.week_number
          ? `Week ${week.week_number}`
          : null,
        event?.title || 'Event',
        event?.event_time
          ? String(
              event.event_time
            ).slice(0, 5)
          : null,
        event?.location || null
      ].filter(Boolean).join(' · '),
      anchorDate:
        event?.event_date ||
        week?.class_date ||
        null
    };
  }

  function getStructuredFormScheduleTiming({
    enrolment,
    assignment,
    instance,
    groupContext,
    extensionDays
  }) {
    const setting =
      getScheduleSetting(assignment);

    if (
      setting?.schedule_enabled !== true
    ) {
      return null;
    }

    const choiceEnabled =
      setting?.schedule_choice_enabled ===
      true;

    const mode =
      choiceEnabled
        ? 'choice'
        : 'single';

    const items =
      structuredFormScheduleItems
        .filter(
          item =>
            String(
              item.course_structured_instance_id
            ) ===
              String(instance.id) &&
            item.configuration_mode === mode
        )
        .sort(
          (a, b) =>
            (Number(a.display_order) || 0) -
              (Number(b.display_order) || 0) ||
            String(a.id).localeCompare(
              String(b.id)
            )
        );

    let selection = null;
    let item = null;

    if (!choiceEnabled) {
      item = items[0] || null;
    } else if (groupContext.groupNumber) {
      selection =
        structuredFormScheduleSelections.find(
          row =>
            String(
              row.course_structured_instance_id
            ) ===
              String(instance.id) &&
            row.enrolment_id === null &&
            Number(row.group_number) ===
              Number(
                groupContext.groupNumber
              )
        ) || null;

      item =
        items.find(
          candidate =>
            String(candidate.id) ===
            String(
              selection
                ?.course_structured_instance_schedule_item_id
            )
        ) || null;
    } else {
      selection =
        structuredFormScheduleSelections.find(
          row =>
            String(
              row.course_structured_instance_id
            ) ===
              String(instance.id) &&
            String(row.enrolment_id) ===
              String(enrolment.id) &&
            row.group_number === null
        ) || null;

      item =
        items.find(
          candidate =>
            String(candidate.id) ===
            String(
              selection
                ?.course_structured_instance_schedule_item_id
            )
        ) || null;
    }

    if (!item) {
      return {
        choiceEnabled,
        selection,
        item: null,
        source: null,
        openDate: null,
        baseCloseDate: null,
        effectiveCloseDate: null
      };
    }

    const source =
      getStructuredFormScheduleSource(
        item
      );

    const openDate =
      addSignedDaysToDateString(
        source.anchorDate,
        instance.schedule_open_offset_days
      );

    const baseCloseDate =
      addSignedDaysToDateString(
        source.anchorDate,
        instance.schedule_close_offset_days
      );

    return {
      choiceEnabled,
      selection,
      item,
      source,
      openDate,
      baseCloseDate,
      effectiveCloseDate:
        addDaysToDateString(
          baseCloseDate,
          extensionDays
        )
    };
  }

  function getParentAssignmentSubmission(
    enrolmentId,
    assignmentId
  ) {
    const rows =
      assignmentSubmissions.filter(
        submission =>
          submission.enrolment_id ===
            enrolmentId &&
          submission.course_assignment_id ===
            assignmentId &&
          !submission
            .course_assignment_schedule_item_id &&
          !submission
            .structured_form_instance_id
      );

    if (!rows.length) {
      return null;
    }

    const maxExtensionDays =
      rows.reduce(
        (maxValue, row) =>
          Math.max(
            maxValue,
            Math.max(
              0,
              Number(
                row.extension_days
              ) || 0
            )
          ),
        0
      );

    const preferred =
      rows.find(
        row =>
          row.submitted === true ||
          !!row.drive_file_id ||
          !!String(
            row.file_name || ''
          ).trim()
      ) ||
      rows.reduce(
        (best, row) => {
          if (!best) return row;

          return (
            Number(
              row.extension_days
            ) || 0
          ) >
          (
            Number(
              best.extension_days
            ) || 0
          )
            ? row
            : best;
        },
        null
      );

    return {
      ...preferred,
      extension_days:
        maxExtensionDays
    };
  }

  function getStructuredFormSubmission(enrolmentId, assignmentId, instanceId) {
    return assignmentSubmissions.find(
      submission =>
        submission.enrolment_id === enrolmentId &&
        submission.course_assignment_id === assignmentId &&
        String(submission.structured_form_instance_id) === String(instanceId)
    ) || null;
  }

  function getStructuredFormEffectiveExtensionDays(
    enrolmentId,
    assignmentId,
    instanceId
  ) {
    const parent =
      getParentAssignmentSubmission(
        enrolmentId,
        assignmentId
      );

    const assignmentWide =
      Math.max(
        0,
        Number(
          parent?.extension_days
        ) || 0
      );

    const instanceSpecific =
      Math.max(
        0,
        Number(
          parent
            ?.structured_form_instance_extensions
            ?.[String(instanceId)]
        ) || 0
      );

    return Math.max(
      assignmentWide,
      instanceSpecific
    );
  }


  function getOccurrenceAssignmentSubmission(
    enrolmentId,
    assignmentId,
    scheduleItemId
  ) {
    return assignmentSubmissions.find(
      submission =>
        submission.enrolment_id === enrolmentId &&
        submission.course_assignment_id === assignmentId &&
        String(submission.course_assignment_schedule_item_id) ===
          String(scheduleItemId)
    ) || null;
  }

  function occurrenceSubmissionHasWork(submission) {
    return !!(
      submission &&
      (
        submission.drive_file_id ||
        submission
          .structured_form_snapshot ||
        String(
          submission.text || ''
        )
          .replace(/<[^>]*>/g, '')
          .trim()
      )
    );
  }

  function getActiveScheduleItems(
    assignment,
    choiceEnabled
  ) {
    const mode = choiceEnabled ? 'choice' : 'single';

    return (assignmentScheduleContext.scheduleItems || [])
      .filter(
        item =>
          String(item.course_assignment_id) ===
            String(assignment.id) &&
          item.configuration_mode === mode
      )
      .sort(
        (a, b) => {
          const aTime =
            getScheduleItemChronologyValue(
              a,
              assignmentScheduleContext
            );

          const bTime =
            getScheduleItemChronologyValue(
              b,
              assignmentScheduleContext
            );


          if (
            aTime !== null &&
            bTime !== null &&
            aTime !== bTime
          ) {
            return aTime - bTime;
          }


          if (
            aTime !== null &&
            bTime === null
          ) {
            return -1;
          }


          if (
            aTime === null &&
            bTime !== null
          ) {
            return 1;
          }


          return (
            (Number(a.display_order) || 0) -
              (Number(b.display_order) || 0) ||
            String(a.id).localeCompare(
              String(b.id)
            )
          );
        }
      );
  }

  function getScheduleItemForEnrolment(
    enrolment,
    assignment
  ) {
    const setting = getScheduleSetting(assignment);

    if (setting?.schedule_enabled !== true) {
      return null;
    }

    const historicalSubmission = assignmentSubmissions
      .filter(
        submission =>
          String(submission.enrolment_id) === String(enrolment.id) &&
          String(submission.course_assignment_id) === String(assignment.id) &&
          submission.course_assignment_schedule_item_id &&
          occurrenceSubmissionHasWork(submission)
      )
      .sort((a, b) =>
        new Date(
          b.submitted_at || b.updated_at || b.created_at || 0
        ).getTime() -
        new Date(
          a.submitted_at || a.updated_at || a.created_at || 0
        ).getTime()
      )[0];

    if (historicalSubmission) {
      const historicalItem =
        (assignmentScheduleContext.scheduleItems || []).find(
          item =>
            String(item.id) ===
            String(
              historicalSubmission.course_assignment_schedule_item_id
            )
        );

      if (historicalItem) return historicalItem;
    }

    const choiceEnabled =
      isScheduleChoiceEnabledForAssignment({
        assignmentId: assignment.id,
        setting,
        context: assignmentScheduleContext,
        isMaster: false
      });

    const activeItems =
      getActiveScheduleItems(
        assignment,
        choiceEnabled
      );

    if (!choiceEnabled) {
      return activeItems[0] || null;
    }

    const usesGroups =
      assignment.groups_enabled === true &&
      Number(assignment.group_count) > 0;

    let selection = null;

    if (usesGroups) {
      const membership = assignmentGroupMemberships.find(
        row =>
          String(row.course_assignment_id) ===
            String(assignment.id) &&
          String(row.enrolment_id) === String(enrolment.id)
      );

      if (!membership) return null;

      selection = assignmentScheduleSelections.find(
        row =>
          String(row.course_assignment_id) ===
            String(assignment.id) &&
          row.enrolment_id === null &&
          Number(row.group_number) ===
            Number(membership.group_number)
      );
    } else {
      selection = assignmentScheduleSelections.find(
        row =>
          String(row.course_assignment_id) ===
            String(assignment.id) &&
          String(row.enrolment_id) === String(enrolment.id) &&
          row.group_number === null
      );
    }

    if (!selection) return null;

    return activeItems.find(
      item =>
        String(item.id) ===
        String(selection.course_assignment_schedule_item_id)
    ) || null;
  }

  function getScheduleWindowStatus(dates) {
    const today = getToday();
    if (!dates?.openDate || !dates?.dueDate) return 'not_configured';
    if (today < dates.openDate) return 'not_open';
    return today <= dates.dueDate ? 'open' : 'closed';
  }

  let columns = [];

  if (selectedType === 'Attendance') {
    const scheduledWeekIds = course.group_based_attendance
      ? new Set(
          attendanceGroupWeeks.map(row => row.course_week_id)
        )
      : null;

    columns = weeks
      .filter(week =>
        course.group_based_attendance
          ? scheduledWeekIds.has(week.id)
          : week.attendance_enabled
      )
      .map(week => ({
        key: week.id,
        kind: 'attendance',
        week
      }));
  } else if (selectedType === 'Critical Questions') {
    columns = weeks
      .filter(week => week.cq_enabled)
      .map(week => ({
        key: week.id,
        kind: 'cq',
        week
      }));
  } else if (selectedType === 'Philosophical Diary') {
    columns = weeks
      .filter(week => week.pd_enabled)
      .map(week => ({
        key: week.id,
        kind: 'pd',
        week
      }));
  } else if (selectedType === 'Self-Evaluation') {
    columns = [
      {
        key: 'self-evaluation-mid',
        kind: 'selfeval',
        section: 'Mid-Semester'
      },
      {
        key: 'self-evaluation-final',
        kind: 'selfeval',
        section: 'Final'
      }
    ];
   } else if (activeAssignmentGroup) {
    columns = activeAssignmentGroup.assignments.flatMap(assignment => {
      if (assignment.assignment_types?.assignment_kind !== 'structured_form') {
        return [{ key: assignment.id, kind: 'assignment', assignment }];
      }

      return structuredFormInstances
        .filter(instance => instance.course_assignment_id === assignment.id)
        .map(instance => ({
          key: `${assignment.id}:${instance.id}`,
          kind: 'assignment',
          assignment,
          structuredFormInstance: instance
        }));
    });
  }

  function buildAssignmentNavigationEntry(
    enrolment,
    column
  ) {
    if (
      !column ||
      column.kind !== 'assignment'
    ) {
      return null;
    }

    const assignment =
      column.assignment;

    const requiresSubmission =
      assignment?.requires_submission ??
      assignment?.assignment_types
        ?.requires_submission ??
      true;

    if (!requiresSubmission) {
      return null;
    }


    // --------------------------------------------------------
    // Structured Form occurrence
    // --------------------------------------------------------

    if (
      column.structuredFormInstance
    ) {
      const instance =
        column.structuredFormInstance;

      const groupContext =
        getStructuredFormGroupContext(
          enrolment.id,
          assignment
        );

      const submissionEnrolmentId =
        groupContext.ownerEnrolmentId;

      const sub =
        getStructuredFormSubmission(
          submissionEnrolmentId,
          assignment.id,
          instance.id
        );

      if (!sub?.submitted) {
        return null;
      }

      const setting =
        getScheduleSetting(
          assignment
        );

      const scheduleEnabled =
        setting?.schedule_enabled ===
        true;

      const groupDeadlinesEnabled =
        !scheduleEnabled &&
        setting?.group_deadlines_enabled ===
          true;

      const groupDeadline =
        groupDeadlinesEnabled
          ? getStructuredFormGroupDeadline(
              instance.id,
              groupContext.groupNumber
            )
          : null;

      const extensionDays =
        getStructuredFormEffectiveExtensionDays(
          submissionEnrolmentId,
          assignment.id,
          instance.id
        );

      const scheduleTiming =
        scheduleEnabled
          ? getStructuredFormScheduleTiming({
              enrolment,
              assignment,
              instance,
              groupContext,
              extensionDays
            })
          : null;

      const baseOpen =
        scheduleEnabled
          ? scheduleTiming?.openDate
          : (
              groupDeadline?.open_date ||
              instance.open_date
            );

      const baseClose =
        scheduleEnabled
          ? scheduleTiming?.baseCloseDate
          : (
              groupDeadline?.close_date ||
              instance.close_date
            );

      return {
        submission: sub,

        feedback:
          latestFeedback(
            sub.assignment_feedback
          ),

        target: {
          type: 'assignment',

          enrolmentId:
            submissionEnrolmentId,

          gradeOverrideEnrolmentId:
            enrolment.id,

          assignmentId:
            assignment.id,

          structuredFormInstanceId:
            instance.id,

          structuredFormInstanceOrder:
            instance.instance_order,

          structuredFormOpenDate:
            baseOpen,

          structuredFormCloseDate:
            baseClose,

          structuredFormScheduleSourceLabel:
            scheduleTiming?.source
              ?.label || null,

          structuredFormScheduleAnchorDate:
            scheduleTiming?.source
              ?.anchorDate || null,

          assignmentGroupNumber:
            groupContext.groupNumber
        }
      };
    }


    const setting =
      getScheduleSetting(
        assignment
      );


    // --------------------------------------------------------
    // Scheduled ordinary assignment
    // --------------------------------------------------------

    if (
      setting?.schedule_enabled ===
      true
    ) {
      const item =
        getScheduleItemForEnrolment(
          enrolment,
          assignment
        );

      if (!item) {
        return null;
      }

      const sub =
        getOccurrenceAssignmentSubmission(
          enrolment.id,
          assignment.id,
          item.id
        );

      if (!sub?.submitted) {
        return null;
      }

      const parentSub =
        getParentAssignmentSubmission(
          enrolment.id,
          assignment.id
        );

      const dates =
        getScheduleItemDates(
          item,
          setting,
          assignmentScheduleContext,
          parentSub?.extension_days ||
            0
        );

      const source =
        getScheduleItemSource(
          item,
          assignmentScheduleContext
        );

      return {
        submission: sub,

        feedback:
          latestFeedback(
            sub.assignment_feedback
          ),

        target: {
          type: 'assignment',

          enrolmentId:
            enrolment.id,

          assignmentId:
            assignment.id,

          scheduleItemId:
            item.id,

          scheduleSourceTitle:
            source?.title ||
            'Schedule item',

          scheduleSourceMeta:
            source?.meta || '',

          scheduleOpenDate:
            dates?.openDate || null,

          scheduleDueDate:
            dates?.dueDate || null
        }
      };
    }


    // --------------------------------------------------------
    // Ordinary assignment
    // --------------------------------------------------------

    const sub =
      getParentAssignmentSubmission(
        enrolment.id,
        assignment.id
      );

    if (!sub?.submitted) {
      return null;
    }

    return {
      submission: sub,

      feedback:
        latestFeedback(
          sub.assignment_feedback
        ),

      target: {
        type: 'assignment',

        enrolmentId:
          enrolment.id,

        assignmentId:
          assignment.id
      }
    };
  }


  // DISTINCT_GROUP_GRADING_NAV_V1
  function getAssignmentNavigationUnitKey(
    entry,
    column
  ) {
    if (!entry) {
      return null;
    }

    const assignment =
      column?.assignment;

    const submissionId =
      String(
        entry.submission?.id ||
        ''
      );

    /*
     * Structured Forms already use a canonical shared
     * workspace/submission for grouped work.
     */
    if (
      entry.target
        ?.structuredFormInstanceId
    ) {
      return submissionId
        ? 'submission:' +
            submissionId
        : [
            'structured',
            entry.target
              ?.assignmentId ||
              '',
            entry.target
              ?.structuredFormInstanceId ||
              '',
            entry.target
              ?.enrolmentId ||
              ''
          ].join(':');
    }

    /*
     * Ordinary/scheduled group submissions have one
     * synchronized submission row per member.
     *
     * For grading navigation they are ONE grading unit:
     *
     * assignment + group + scheduled occurrence.
     */
    const isGroupSubmission =
      assignment
        ?.groups_enabled ===
        true &&
      Number(
        assignment
          ?.group_count ||
        0
      ) > 0 &&
      assignment
        ?.assignment_types
        ?.group_submission ===
        true;

    if (isGroupSubmission) {
      const enrolmentId =
        entry.target
          ?.gradeOverrideEnrolmentId ||
        entry.target
          ?.enrolmentId;

      const membership =
        (
          assignmentGroupMemberships ||
          []
        ).find(
          row =>
            String(
              row
                .course_assignment_id ||
              ''
            ) ===
              String(
                assignment?.id ||
                ''
              ) &&

            String(
              row
                .enrolment_id ||
              ''
            ) ===
              String(
                enrolmentId ||
                ''
              )
        );

      const groupNumber =
        membership
          ?.group_number;

      if (
        groupNumber !==
          null &&
        groupNumber !==
          undefined
      ) {
        return [
          'group',
          assignment.id,
          groupNumber,
          'schedule',
          entry.target
            ?.scheduleItemId ||
            'base'
        ].join(':');
      }
    }

    /*
     * Individual work remains one grading unit per
     * actual submission.
     */
    if (submissionId) {
      return (
        'submission:' +
        submissionId
      );
    }

    return [
      'target',
      entry.target
        ?.enrolmentId ||
        '',
      entry.target
        ?.assignmentId ||
        '',
      entry.target
        ?.structuredFormInstanceId ||
        '',
      entry.target
        ?.scheduleItemId ||
        ''
    ].join(':');
  }


  function assignmentNavigationEntryMatchesTarget(
    entry,
    target
  ) {
    if (
      !entry ||
      !target
    ) {
      return false;
    }

    return (
      String(
        entry.target
          ?.enrolmentId ||
        ''
      ) ===
        String(
          target
            ?.enrolmentId ||
          ''
        ) &&

      String(
        entry.target
          ?.assignmentId ||
        ''
      ) ===
        String(
          target
            ?.assignmentId ||
          ''
        ) &&

      String(
        entry.target
          ?.structuredFormInstanceId ||
        ''
      ) ===
        String(
          target
            ?.structuredFormInstanceId ||
          ''
        ) &&

      String(
        entry.target
          ?.scheduleItemId ||
        ''
      ) ===
        String(
          target
            ?.scheduleItemId ||
          ''
        )
    );
  }


  // RESPONSIBLE_GRADING_NAV_V1

  function getNavigationEntryEnrolment(
    entry
  ) {
    const enrolmentId =
      entry?.target
        ?.gradeOverrideEnrolmentId ||
      entry?.target
        ?.enrolmentId ||
      '';

    if (!enrolmentId) {
      return null;
    }

    return (
      enrolments.find(
        enrolment =>
          String(
            enrolment.id
          ) ===
          String(
            enrolmentId
          )
      ) ||
      null
    );
  }


  function assignmentEntryBelongsToCurrentInstructor(
    entry,
    column
  ) {
    const instructorId =
      gradingSession
        ?.instructorId ||
      '';

    if (!instructorId) {
      return false;
    }

    const enrolment =
      getNavigationEntryEnrolment(
        entry
      );

    if (!enrolment) {
      return false;
    }

    /*
     * This intentionally mirrors
     * private.instructor_grading_queue:
     *
     * assignment instructor
     * -> student instructor
     * -> primary course instructor.
     */
    const effectiveInstructorId =
      column?.assignment
        ?.assigned_instructor_id ||
      enrolment
        .assigned_instructor_id ||
      course
        ?.instructor_id ||
      '';

    return (
      String(
        effectiveInstructorId
      ) ===
      String(
        instructorId
      )
    );
  }


  function pdBelongsToCurrentInstructor(
    enrolment,
    week
  ) {
    const instructorId =
      gradingSession
        ?.instructorId ||
      '';

    if (
      !instructorId ||
      !enrolment ||
      !week
    ) {
      return false;
    }

    /*
     * Same precedence as the live grading queue:
     *
     * PD week instructor
     * -> student instructor
     * -> primary course instructor.
     */
    const effectiveInstructorId =
      week
        .pd_assigned_instructor_id ||
      enrolment
        .assigned_instructor_id ||
      course
        ?.instructor_id ||
      '';

    return (
      String(
        effectiveInstructorId
      ) ===
      String(
        instructorId
      )
    );
  }


  function getAdjacentUngradedAssignmentTarget(
    direction
  ) {
    if (
      gradingTarget?.type !==
      'assignment'
    ) {
      return null;
    }

    const column =
      columns.find(
        candidate =>
          candidate.kind ===
            'assignment' &&

          String(
            candidate
              .assignment?.id ||
            ''
          ) ===
            String(
              gradingTarget
                .assignmentId ||
              ''
            ) &&

          (
            gradingTarget
              .structuredFormInstanceId

              ? String(
                  candidate
                    .structuredFormInstance
                    ?.id ||
                  ''
                ) ===
                  String(
                    gradingTarget
                      .structuredFormInstanceId
                  )

              : !candidate
                  .structuredFormInstance
          )
      ) || null;

    if (!column) {
      return null;
    }


    /*
     * First collect the raw roster entries.
     *
     * For ordinary group assignments this can contain
     * several synchronized submission rows for the same
     * actual piece of work.
     */
    const rawEntries =
      enrolments
        .map(
          enrolment =>
            buildAssignmentNavigationEntry(
              enrolment,
              column
            )
        )
        .filter(Boolean);


    const currentEntry =
      rawEntries.find(
        entry =>
          assignmentNavigationEntryMatchesTarget(
            entry,
            gradingTarget
          )
      );

    if (!currentEntry) {
      return null;
    }


    const currentUnitKey =
      getAssignmentNavigationUnitKey(
        currentEntry,
        column
      );

    if (!currentUnitKey) {
      return null;
    }


    /*
     * Collapse synchronized group-member rows into their
     * actual grading units.
     */
    const units = [];

    const unitByKey =
      new Map();

    rawEntries.forEach(
      entry => {
        const key =
          getAssignmentNavigationUnitKey(
            entry,
            column
          );

        if (!key) {
          return;
        }

        let unit =
          unitByKey.get(
            key
          );

        if (!unit) {
          unit = {
            key,
            target:
              entry.target,

            responsibleTarget:
              null,

            entries: []
          };

          unitByKey.set(
            key,
            unit
          );

          units.push(
            unit
          );
        }

        unit.entries.push(
          entry
        );

        if (
          !unit.responsibleTarget &&
          assignmentEntryBelongsToCurrentInstructor(
            entry,
            column
          )
        ) {
          unit.responsibleTarget =
            entry.target;
        }

        /*
         * If this is the grading unit currently open,
         * keep the exact current target as its navigation
         * representative.
         */
        if (
          assignmentNavigationEntryMatchesTarget(
            entry,
            gradingTarget
          )
        ) {
          unit.target =
            entry.target;
        }
      }
    );


    const currentIndex =
      units.findIndex(
        unit =>
          unit.key ===
          currentUnitKey
      );

    if (currentIndex < 0) {
      return null;
    }


    const step =
      direction ===
        'previous'
        ? -1
        : 1;


    for (
      let index =
        currentIndex +
        step;

      index >= 0 &&
      index < units.length;

      index += step
    ) {
      const unit =
        units[index];

      /*
       * Skip grading units assigned to another instructor.
       *
       * For group work, any member row assigned to this
       * instructor makes the shared grading unit part of
       * this instructor's queue, matching the database
       * grading-unit logic.
       */
      if (
        !unit.responsibleTarget
      ) {
        continue;
      }

      /*
       * A grading unit is finished if ANY synchronized
       * member row shows submitted feedback or completed
       * unpublished grading.
       *
       * This prevents another member of an already graded
       * group from being treated as a new task.
       */
      const graded =
        unit.entries.some(
          entry =>
            entry.feedback
              ?.submitted ===
              true ||

            submissionHasUnpublishedGrade(
              instructorFeedbackRows,
              entry.submission?.id
            )
        );

      if (!graded) {
        return unit.responsibleTarget;
      }
    }

    return null;
  }


  function getAdjacentUngradedPdTarget(
    direction
  ) {
    if (
      gradingTarget?.type !== 'pd'
    ) {
      return null;
    }

    const week =
      weeks.find(
        row =>
          String(
            row.id
          ) ===
          String(
            gradingTarget
              .weekId
          )
      ) ||
      null;

    if (!week) {
      return null;
    }

    const entries =
      enrolments
        .map(enrolment => {
          const submission =
            pdSubmissions.find(
              row =>
                String(
                  row.enrolment_id
                ) ===
                  String(
                    enrolment.id
                  ) &&
                String(
                  row.week_id
                ) ===
                  String(
                    gradingTarget.weekId
                  )
            );

          if (!submission?.submitted) {
            return null;
          }

          return {
            enrolment,

            submission,

            feedback:
              latestFeedback(
                submission.pd_feedback
              ),

            target: {
              type: 'pd',
              enrolmentId:
                enrolment.id,
              weekId:
                gradingTarget.weekId
            }
          };
        })
        .filter(Boolean);

    const currentIndex =
      entries.findIndex(
        entry =>
          String(
            entry.target
              .enrolmentId
          ) ===
            String(
              gradingTarget
                .enrolmentId
            )
      );

    if (currentIndex < 0) {
      return null;
    }

    const step =
      direction === 'previous'
        ? -1
        : 1;

    for (
      let index =
        currentIndex + step;

      index >= 0 &&
      index < entries.length;

      index += step
    ) {
      const entry =
        entries[index];

      if (
        !pdBelongsToCurrentInstructor(
          entry.enrolment,
          week
        )
      ) {
        continue;
      }

      if (
        entry.feedback?.submitted !==
        true
      ) {
        return entry.target;
      }
    }

    return null;
  }


  function getAdjacentUnreviewedSelfEvalTarget(direction) {
    if (gradingTarget?.type !== 'selfeval') return null;

    const section = gradingTarget.section;

    const entries = enrolments.filter(enrolment =>
      selfEvalResponses.some(response =>
        String(response.enrolment_id) === String(enrolment.id) &&
        response.section === section &&
        response.status === 'Submitted'
      )
    );

    const currentIndex = entries.findIndex(enrolment =>
      String(enrolment.id) === String(gradingTarget.enrolmentId)
    );

    if (currentIndex < 0) return null;

    const step = direction === 'previous' ? -1 : 1;

    for (
      let index = currentIndex + step;
      index >= 0 && index < entries.length;
      index += step
    ) {
      const enrolment = entries[index];

      const responsibleId =
        enrolment.assigned_instructor_id || course.instructor_id;

      if (
        String(responsibleId || '') !==
        String(gradingSession?.instructorId || '')
      ) {
        continue;
      }

      const response = selfEvalResponses.find(row =>
        String(row.enrolment_id) === String(enrolment.id) &&
        row.section === section
      );

      if (!selfEvalReviewIsComplete(response)) {
        return {
          type: 'selfeval',
          enrolmentId: enrolment.id,
          section
        };
      }
    }

    return null;
  }

  const previousUngradedTarget =
    gradingTarget?.type === 'selfeval'
      ? getAdjacentUnreviewedSelfEvalTarget('previous')
      : gradingTarget?.type === 'pd'
        ? getAdjacentUngradedPdTarget('previous')
        : getAdjacentUngradedAssignmentTarget('previous');

  const nextUngradedTarget =
    gradingTarget?.type === 'selfeval'
      ? getAdjacentUnreviewedSelfEvalTarget('next')
      : gradingTarget?.type === 'pd'
        ? getAdjacentUngradedPdTarget('next')
        : getAdjacentUngradedAssignmentTarget('next');

  const hasStudentInstructorAssignments =
    instructors.length > 1 &&
    enrolments.some(
      enrolment =>
        !!enrolment.assigned_instructor_id
    );

  const pdUsesInstanceInstructorAssignments =
    selectedType === 'Philosophical Diary' &&
    weeks.some(
      week =>
        week.pd_enabled &&
        !!week.pd_assigned_instructor_id
    );

  const assignmentUsesInstanceInstructorAssignments =
    !!activeAssignmentGroup &&
    activeAssignmentGroup.assignments.some(
      assignment =>
        !!assignment.assigned_instructor_id
    );

  const usesInstanceInstructorAssignments =
    pdUsesInstanceInstructorAssignments ||
    assignmentUsesInstanceInstructorAssignments;

  const showStudentInstructorColumn =
    selectedType !== 'Attendance' &&
    hasStudentInstructorAssignments &&
    !usesInstanceInstructorAssignments;

  const horizontalScroll =
    useTopHorizontalScrollbar(
      [
        selectedType,
        columns.length,
        enrolments.length,

        showStudentInstructorColumn
          ? 1
          : 0
      ].join(':')
    );



  function builtInExtensionLabel(
    column
  ){
    return column.kind==='pd'
      ?(
          'Philosophical Diary — Week '+
          (
            column.week
              ?.week_number ??
            '—'
          )
        )
      :(
          'Self-Evaluation ('+
          column.section+
          ')'
        );
  }


  function openBuiltInExtension(
    column
  ){
    if(
      column.kind==='selfeval'
    ){
      setBuiltInExtension({
        column,

        days:
          String(
            Math.max(
              0,
              Number(
                selfEvalExtensionDays[
                  column.section
                ]
              )||0
            )
          ),

        mixed:false,
        saving:false,
        error:'',
        notice:''
      });

      return;
    }


    const targets=
      (enrolments||[])
        .filter(
          e=>
            e?.students
              ?.is_master!==true
        )
        .map(
          e=>{
            const sub=
              (
                pdSubmissions||[]
              ).find(
                r=>
                  String(
                    r.enrolment_id
                  )===
                    String(e.id) &&
                  String(
                    r.week_id
                  )===
                    String(
                      column.week.id
                    )
              );

            return {
              enrolmentId:
                e.id,

              days:
                Math.max(
                  0,
                  Number(
                    sub
                      ?.extension_days
                  )||0
                )
            };
          }
        );


    if(!targets.length){
      setRefreshError(
        'There are no active students for this extension.'
      );

      return;
    }


    const vals=[
      ...new Set(
        targets.map(
          t=>t.days
        )
      )
    ];


    setBuiltInExtension({
      column,

      days:
        vals.length===1
          ?String(vals[0])
          :'',

      mixed:
        vals.length>1,

      saving:false,
      error:'',
      notice:''
    });
  }


  async function saveBuiltInExtension(){
    if(
      !builtInExtension ||
      builtInExtension.saving
    ){
      return;
    }


    const days=
      Number(
        builtInExtension.days
      );

    const column=
      builtInExtension.column;


    if(
      !Number.isInteger(days) ||
      days<0
    ){
      setBuiltInExtension(
        v=>({
          ...v,

          error:
            'Extension must be a whole number of days, 0 or greater.'
        })
      );

      return;
    }


    setBuiltInExtension(
      v=>({
        ...v,
        saving:true,
        error:'',
        notice:''
      })
    );


    if(
      column.kind==='selfeval'
    ){
      try{
        await saveSelfEvalClassExtensionValue(
          course.id,
          column.section,
          days
        );

        setSelfEvalExtensionDays(
          v=>({
            ...v,
            [column.section]:
              days
          })
        );

        setBuiltInExtension(
          v=>({
            ...v,

            days:
              String(days),

            mixed:false,
            saving:false,

            notice:
              days>0
                ?(
                    'Saved a '+
                    days+
                    '-day extension for the class.'
                  )
                :'Set the class extension to 0 days.'
          })
        );

      }catch(err){
        console.error(err);

        setBuiltInExtension(
          v=>({
            ...v,
            saving:false,

            error:
              err?.message ||
              'Could not save the class extension.'
          })
        );
      }

      return;
    }


    const targets=
      (enrolments||[])
        .filter(
          e=>
            e?.students
              ?.is_master!==true
        )
        .map(
          e=>e.id
        );


    const results=
      await Promise.allSettled(
        targets.map(
          id=>
            savePdClassExtensionValue(
              id,
              column.week.id,
              days
            )
        )
      );


    const failed=
      results.filter(
        r=>
          r.status==='rejected'
      );


    try{
      await refreshData();

    }catch(err){
      console.error(
        'Refresh after PD extension failed:',
        err
      );
    }


    if(failed.length){
      setBuiltInExtension(
        v=>({
          ...v,
          saving:false,

          error:
            'Saved for '+
            (
              targets.length-
              failed.length
            )+
            ' of '+
            targets.length+
            ' student records. '+
            failed.length+
            ' failed.'
        })
      );

      return;
    }


    setBuiltInExtension(
      v=>({
        ...v,

        days:
          String(days),

        mixed:false,
        saving:false,

        notice:
          days>0
            ?(
                'Saved a '+
                days+
                '-day extension for the class.'
              )
            :'Set the class extension to 0 days.'
      })
    );
  }


  function assignmentAllowsClassExtension(assignment) {
    return (assignment?.requires_submission ?? assignment?.assignment_types?.requires_submission ?? true) !== false;
  }

  function assignmentColumnLabel(column) {
    const assignment = column?.assignment;
    if (!assignment) return 'Assignment';

    const base = activeAssignmentGroup?.name || assignment.assignment_types?.name || 'Assignment';
    const label = activeAssignmentGroup?.assignments.length > 1
      ? base + ' ' + (Number(assignment.instance_number) || 1)
      : base;

    return column.structuredFormInstance
      ? label + ' — Instance ' + column.structuredFormInstance.instance_order
      : label;
  }

  function classExtensionTargets(column) {
    if (!column?.assignment) return [];

    const assignment = column.assignment;
    const structured = column.structuredFormInstance || null;
    const targets = new Map();

    (enrolments || [])
      .filter(row => row?.students?.is_master !== true)
      .forEach(enrolment => {
        let enrolmentId = enrolment.id;

        if (structured) {
          const groupContext = getStructuredFormGroupContext(enrolment.id, assignment);
          enrolmentId = groupContext.workspace?.owner_enrolment_id || enrolmentId;
        }

        const key = String(enrolmentId || '');
        if (!key || targets.has(key)) return;

        const parent = getParentAssignmentSubmission(enrolmentId, assignment.id);
        const instanceExtensions =
          parent?.structured_form_instance_extensions &&
          typeof parent.structured_form_instance_extensions === 'object' &&
          !Array.isArray(parent.structured_form_instance_extensions)
            ? parent.structured_form_instance_extensions
            : {};

        const days = structured
          ? Math.max(0, Number(instanceExtensions[String(structured.id)]) || 0)
          : Math.max(0, Number(parent?.extension_days) || 0);

        targets.set(key, { enrolmentId, days, instanceExtensions });
      });

    return [...targets.values()];
  }

  function openClassExtension(column) {
    const targets = classExtensionTargets(column);
    if (!targets.length) {
      setRefreshError('There are no active students for this assignment.');
      return;
    }

    const values = [...new Set(targets.map(target => target.days))];
    setClassExtension({
      column,
      days: values.length === 1 ? String(values[0]) : '',
      mixed: values.length > 1,
      saving: false,
      error: '',
      notice: ''
    });
  }

  async function saveClassExtension() {
    if (!classExtension || classExtension.saving) return;

    const days = Number(classExtension.days);
    if (!Number.isInteger(days) || days < 0) {
      setClassExtension(value => ({ ...value, error: 'Extension must be a whole number of days, 0 or greater.' }));
      return;
    }

    const column = classExtension.column;
    const assignment = column.assignment;
    const instanceId = column.structuredFormInstance?.id || null;
    const targets = classExtensionTargets(column);

    setClassExtension(value => ({ ...value, saving: true, error: '', notice: '' }));

    const results = await Promise.allSettled(
      targets.map(target => saveClassExtensionValue({
        enrolmentId: target.enrolmentId,
        assignmentId: assignment.id,
        days,
        instanceId,
        currentInstanceExtensions: target.instanceExtensions
      }))
    );

    const failures = results.filter(result => result.status === 'rejected');
    try { await refreshData(); } catch (error) { console.error('Refresh after class extension failed:', error); }

    if (failures.length) {
      console.error('Some class extension saves failed:', failures.map(result => result.reason));
      setClassExtension(value => ({
        ...value,
        saving: false,
        error: 'Saved for ' + (targets.length - failures.length) + ' of ' + targets.length + ' student/group records. ' + failures.length + ' failed.'
      }));
      return;
    }

    setClassExtension(value => ({
      ...value,
      days: String(days),
      mixed: false,
      saving: false,
      notice: days > 0 ? 'Saved a ' + days + '-day extension for the class.' : 'Set the class extension to 0 days.'
    }));
  }

  function renderEffectiveInstructorBadge(
    enrolment,
    explicitInstructorId
  ) {
    if (!usesInstanceInstructorAssignments) {
      return null;
    }

    const effectiveInstructorId =
      explicitInstructorId ||
      enrolment.assigned_instructor_id;

    const instructorIndex =
      instructors.findIndex(
        instructor =>
          instructor.id ===
          effectiveInstructorId
      );

    if (instructorIndex < 0) {
      return (
        <span
          style={styles.unassignedInstructor}
          title="No instructor assigned"
        >
          —
        </span>
      );
    }

    const instructor =
      instructors[instructorIndex];

    const badgeStyle =
      INSTRUCTOR_BADGES[
        instructorIndex %
          INSTRUCTOR_BADGES.length
      ];

    return (
      <span
        style={{
          ...styles.instructorBadge,
          ...badgeStyle
        }}
        title={
          explicitInstructorId
            ? `${instructor.name} — instance assignment`
            : `${instructor.name} — student assignment`
        }
      >
        {getInstructorInitials(
          instructor.name
        )}
      </span>
    );
  }

  function hasUnpublishedGradesForAssignment(
    assignmentId
  ) {
    return unpublishedGradingStatus.some(
      row =>
        String(
          row.course_assignment_id ||
          ''
        ) ===
        String(
          assignmentId ||
          ''
        )
    );
  }


  async function handlePublishGrades(
    assignmentId
  ) {
    if (
      !assignmentId ||
      publishingAssignmentId
    ) {
      return;
    }

    setPublishingAssignmentId(
      assignmentId
    );
    setRefreshError('');

    try {
      await publishCourseAssignmentGrades(
        assignmentId
      );

      try {
        await notifyReleasedIncomplete({
          session: gradingSession,
          courseId: course.id,
          assignmentId,
          submissionType: 'assignment'
        });
      } catch (error) {
        console.error('Grades published; Incomplete push failed:', error);
      }

      await refreshData();
    } catch (err) {
      console.error(
        'Publishing grades failed:',
        err
      );

      setRefreshError(
        err?.message ||
        'Could not publish grades.'
      );
    } finally {
      setPublishingAssignmentId(
        null
      );
    }
  }


  function renderColumnHeader(column) {
    if (
      column.kind === 'attendance' ||
      column.kind === 'cq' ||
      column.kind === 'pd'
    ) {
      const week = column.week;

      const alreadyAssigned =
        qiAssignments.some(
          assignment =>
            assignment.course_week_id ===
            week.id
        );

      return (
        <th
          key={column.key}
          style={styles.thWeek}
        >
          <div
            style={
              styles
                .assignmentHeaderLine
            }
          >
            <span>
              W{week.week_number}
            </span>

            {column.kind==='pd'&&(
              <button
                type="button"
                style={
                  styles
                    .classExtensionPlus
                }
                onClick={()=>
                  openBuiltInExtension(
                    column
                  )
                }
                title="Class extension"
                aria-label={
                  'Class extension for '+
                  builtInExtensionLabel(
                    column
                  )
                }
              >
                +
              </button>
            )}
          </div>

          <div style={styles.weekDate}>
            {week.class_date?.slice(5)}
          </div>

          {column.kind === 'cq' &&
            course.qi_enabled &&
            canManageCourse && (
              <div style={styles.qiHeader}>
                <span style={styles.qiLabel}>
                  QI
                </span>

                <button
                  type="button"
                  style={{
                    ...styles.qiAssignBtn,
                    opacity:
                      alreadyAssigned
                        ? 0.4
                        : 1,
                    cursor:
                      alreadyAssigned
                        ? 'default'
                        : 'pointer'
                  }}
                  disabled={
                    assigningQi ||
                    alreadyAssigned
                  }
                  onClick={() =>
                    handleAssignQi(
                      week.id
                    )
                  }
                  title={
                    alreadyAssigned
                      ? 'Already assigned'
                      : 'Assign questions'
                  }
                >
                  {alreadyAssigned
                    ? '✓'
                    : 'Assign'}
                </button>
              </div>
            )}
        </th>
      );
    }

    if (column.kind === 'selfeval') {
      return (
        <th
          key={column.key}
          style={styles.thWeek}
        >
          <div
            style={
              styles
                .assignmentHeaderLine
            }
          >
            <span>
              {column.section}
            </span>

            <button
              type="button"
              style={
                styles
                  .classExtensionPlus
              }
              onClick={()=>
                openBuiltInExtension(
                  column
                )
              }
              title="Class extension"
              aria-label={
                'Class extension for '+
                builtInExtensionLabel(
                  column
                )
              }
            >
              +
            </button>
          </div>
        </th>
      );
    }

    if (column.kind === 'assignment') {
      const assignment =
        column.assignment;

      const multiple =
        activeAssignmentGroup
          ?.assignments.length > 1;

      const instance =
        Number(
          assignment.instance_number
        ) || 1;

      if (column.structuredFormInstance) {
        return (
          <th key={column.key} style={styles.thWeek}>
            <div style={styles.assignmentHeaderLine}>
              <span>
                {multiple
                  ? activeAssignmentGroup.name + ' ' + instance
                  : activeAssignmentGroup.name}
              </span>
              {assignmentAllowsClassExtension(assignment) && (
                <button
                  type="button"
                  style={styles.classExtensionPlus}
                  onClick={() => openClassExtension(column)}
                  title="Class extension"
                  aria-label={'Class extension for ' + assignmentColumnLabel(column)}
                >
                  +
                </button>
              )}
            </div>

            {assignment
              .grade_publication_mode ===
                'manual' &&
              hasUnpublishedGradesForAssignment(
                assignment.id
              ) &&
              columns.find(
                candidate =>
                  candidate.kind ===
                    'assignment' &&
                  String(
                    candidate.assignment
                      ?.id || ''
                  ) ===
                    String(
                      assignment.id
                    )
              )?.key ===
                column.key && (
                <button
                  type="button"
                  style={
                    styles.publishGradesBtn
                  }
                  disabled={
                    publishingAssignmentId ===
                    assignment.id
                  }
                  onClick={() =>
                    handlePublishGrades(
                      assignment.id
                    )
                  }
                >
                  {publishingAssignmentId ===
                    assignment.id
                    ? 'Publishing…'
                    : 'Publish Grades'}
                </button>
              )}

            <div style={styles.weekDate}>
              Instance {column.structuredFormInstance.instance_order}
            </div>
            {getScheduleSetting(assignment)
              ?.schedule_enabled === true ? (
              <div style={styles.weekDate}>
                Scheduled
              </div>
            ) : (
              column.structuredFormInstance.close_date && (
                <div style={styles.weekDate}>
                  Due {column.structuredFormInstance.close_date.slice(5)}
                </div>
              )
            )}
          </th>
        );
      }

      return (
        <th
          key={column.key}
          style={styles.thWeek}
        >
          <div style={styles.assignmentHeaderLine}>
            <span>
              {multiple
                ? activeAssignmentGroup.name + ' ' + instance
                : activeAssignmentGroup.name}
            </span>
            {assignmentAllowsClassExtension(assignment) && (
              <button
                type="button"
                style={styles.classExtensionPlus}
                onClick={() => openClassExtension(column)}
                title="Class extension"
                aria-label={'Class extension for ' + assignmentColumnLabel(column)}
              >
                +
              </button>
            )}
          </div>

          {assignment
            .grade_publication_mode ===
              'manual' &&
            hasUnpublishedGradesForAssignment(
              assignment.id
            ) && (
              <button
                type="button"
                style={
                  styles.publishGradesBtn
                }
                disabled={
                  publishingAssignmentId ===
                  assignment.id
                }
                onClick={() =>
                  handlePublishGrades(
                    assignment.id
                  )
                }
              >
                {publishingAssignmentId ===
                  assignment.id
                  ? 'Publishing…'
                  : 'Publish Grades'}
              </button>
            )}

          {getScheduleSetting(assignment)?.schedule_enabled !== true &&
            assignment.due_date && (
            <div style={styles.weekDate}>
              Due{' '}
              {assignment.due_date.slice(
                5
              )}
            </div>
          )}
        </th>
      );
    }

    return null;
  }

  function renderCell(enrolment, column) {
    if (column.kind === 'attendance') {
      const week = column.week;
      const rec = attendance.find(
        record =>
          record.enrolment_id === enrolment.id &&
          record.week_id === week.id
      );

      const present = !!rec?.present;
      const required = isAttendanceRequired(
        enrolment.id,
        week.id
      );

      return (
        <td key={column.key} style={styles.tdCenter}>
          {!required ? (
            <span
              style={styles.futureAtt}
              title="Not scheduled for this attendance group"
            >
              —
            </span>
          ) : (
            <button
              type="button"
              style={{
                ...styles.attBtn,
                ...(present ? styles.attBtnOn : styles.attBtnOff)
              }}
              onClick={() =>
                handleToggleAttendance(
                  enrolment.id,
                  week.id,
                  present
                )
              }
              title={
                present
                  ? 'Mark absent'
                  : 'Mark present'
              }
            >
              {present ? '✓' : ''}
            </button>
          )}
        </td>
      );
    }

    if (column.kind === 'cq') {
      const week =
        column.week;

      const sub =
        cqSubmissions.find(
          submission =>
            submission.enrolment_id ===
              enrolment.id &&
            submission.week_id ===
              week.id
        );

      const manualMark =
        cqManualSubmissionMarks.find(
          row =>
            String(
              row.enrolment_id
            ) ===
              String(
                enrolment.id
              ) &&
            String(
              row.week_id
            ) ===
              String(
                week.id
              )
        );

      const cqSubmitted =
        !!sub ||
        !!manualMark;

      const questionType =
        sub?.question_type ||
        'Critical';

      const qiStatus =
        getQiStatus(
          enrolment,
          week.id
        );

      return (
        <td
          key={column.key}
          style={
            styles.tdCenter
          }
        >
          <div
            style={
              styles.cellStack
            }
          >
            <CellStatus
              status={
                getCqWindowStatus(
                  week,
                  course
                )
              }
              submitted={
                cqSubmitted
              }
              graded={false}
              onClick={
                sub
                  ? () =>
                      openGradingTarget({
                        type: 'cq',
                        enrolmentId:
                          enrolment.id,
                        weekId:
                          week.id
                      })
                  : undefined
              }
            />

            {sub && (
              <button
                type="button"
                style={{
                  ...styles.cqViewButton,

                  ...(
                    questionType ===
                    'Clarificatory'
                      ? styles.cqViewClarificatory
                      : styles.cqViewCritical
                  )
                }}
                title={
                  questionType ===
                    'Clarificatory'
                    ? 'View Clarificatory Question'
                    : 'View Critical Question'
                }
                aria-label={
                  questionType ===
                    'Clarificatory'
                    ? 'View Clarificatory Question'
                    : 'View Critical Question'
                }
                onClick={() =>
                  setCqQuestionViewer({
                    submission:
                      sub,

                    enrolment,

                    week
                  })
                }
              >
                Q
              </button>
            )}

            {!sub && (
              <label
                style={
                  styles.cqManualMark
                }
                title="Mark Critical Question submitted manually"
              >
                <input
                  type="checkbox"
                  checked={
                    !!manualMark
                  }
                  onChange={
                    event =>
                      handleManualCqSubmissionChange(
                        enrolment.id,
                        week.id,
                        event.target
                          .checked
                      )
                  }
                />

                <span>
                  Manual
                </span>
              </label>
            )}

            {course.qi_enabled &&
              qiStatus && (
              <span
                style={{
                  ...styles.qiStatus,

                  ...(
                    qiStatus ===
                    'submitted'
                      ? styles.qiSubmitted
                      : qiStatus ===
                          'draft'
                        ? styles.qiDraft
                        : qiStatus ===
                            'not_in_submission'
                          ? styles.qiNotInSubmission
                          : qiStatus ===
                              'assigned'
                            ? styles.qiAssigned
                          : styles.qiNotAssigned
                  )
                }}
              >
                {qiStatus ===
                  'submitted'
                  ? 'QI ✓'
                  : qiStatus ===
                      'draft'
                    ? 'QI Draft'
                    : qiStatus ===
                        'not_in_submission'
                      ? 'QI Not listed'
                      : qiStatus ===
                          'assigned'
                        ? 'QI —'
                      : ''}
              </span>
            )}
          </div>
        </td>
      );
    }

    if (column.kind === 'pd') {
      const week = column.week;

      const sub = pdSubmissions.find(
        submission =>
          submission.enrolment_id ===
            enrolment.id &&
          submission.week_id ===
            week.id
      );

      const feedback =
        latestFeedback(
          sub?.pd_feedback
        );

      const submitted =
        !!sub?.submitted;

      const graded =
        !!feedback?.submitted;

      return (
        <td
          key={column.key}
          style={styles.tdCenter}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px'
            }}
          >
            {renderEffectiveInstructorBadge(
              enrolment,
              week.pd_assigned_instructor_id
            )}

            <CellStatus
              status={getPdWindowStatus(
                week,
                course,
                sub
              )}
              submitted={submitted}
              graded={graded}
              completionStatus={
                gradingGridCompletionStatus(
                  instructorFeedbackRows,
                  'pd',
                  sub?.id,
                  feedback
                )
              }
              studentResponded={
                feedbackHasStudentResponse(
                  feedback
                )
              }
              allowOpenWithoutSubmission
              onClick={() =>
                openGradingTarget({
                  type: 'pd',
                  enrolmentId:
                    enrolment.id,
                  weekId: week.id
                })
              }
            />
          </div>
        </td>
      );
    }

    if (column.kind === 'selfeval') {
      const response = selfEvalResponses.find(
        row =>
          row.enrolment_id === enrolment.id &&
          row.section === column.section
      );

      const submitted = response?.status === 'Submitted';
      const graded = selfEvalReviewIsComplete(response);

      return (
        <td key={column.key} style={styles.tdCenter}>
          <CellStatus
            status={
              getSelfEvalStatus(
                course,
                column.section,
                selfEvalExtensionDays[
                  column.section
                ]
              )
            }
            submitted={submitted}
            graded={graded}
            displayLabel={
              submitted && graded
                ? selfEvalCompleteGradeLabel(response)
                : null
            }
            onClick={() =>
              openGradingTarget({
                type: 'selfeval',
                enrolmentId: enrolment.id,
                section: column.section
              })
            }
          />
        </td>
      );
    }

    if (column.kind === 'assignment') {
      const assignment = column.assignment;

      const requiresSubmission =
        assignment?.requires_submission ??
        assignment?.assignment_types
          ?.requires_submission ??
        true;

      if (!requiresSubmission) {
        const completion =
          assignmentCompletions.find(
            row =>
              row.enrolment_id === enrolment.id &&
              row.course_assignment_id === assignment.id
          );

        const completed = !!completion?.completed;

        return (
          <td key={column.key} style={styles.tdCenter}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px'
              }}
            >
              {renderEffectiveInstructorBadge(
                enrolment,
                assignment.assigned_instructor_id
              )}

              <input
                type="checkbox"
                checked={completed}
                onChange={() =>
                  handleToggleAssignmentCompletion(
                    enrolment.id,
                    assignment.id,
                    completed
                  )
                }
                title={completed ? 'Completed' : 'Mark completed'}
                aria-label={`${getStudentName(
                  enrolment.students
                )} — ${
                  assignment.assignment_types?.name || 'assignment'
                } completed`}
              />
            </div>
          </td>
        );
      }

      if (column.structuredFormInstance) {
        const instance = column.structuredFormInstance;

        const groupContext =
          getStructuredFormGroupContext(
            enrolment.id,
            assignment
          );

        const submissionEnrolmentId =
          groupContext.ownerEnrolmentId;

        const setting =
          getScheduleSetting(assignment);

        const scheduleEnabled =
          setting?.schedule_enabled === true;

        const groupDeadlinesEnabled =
          !scheduleEnabled &&
          setting?.group_deadlines_enabled ===
            true;

        const groupDeadline =
          groupDeadlinesEnabled
            ? getStructuredFormGroupDeadline(
                instance.id,
                groupContext.groupNumber
              )
            : null;

        const sub = getStructuredFormSubmission(
          submissionEnrolmentId,
          assignment.id,
          instance.id
        );

        const extensionDays =
          getStructuredFormEffectiveExtensionDays(
            submissionEnrolmentId,
            assignment.id,
            instance.id
          );

        const scheduleTiming =
          scheduleEnabled
            ? getStructuredFormScheduleTiming({
                enrolment,
                assignment,
                instance,
                groupContext,
                extensionDays
              })
            : null;

        const baseOpen =
          scheduleEnabled
            ? scheduleTiming?.openDate
            : (
                groupDeadline?.open_date ||
                instance.open_date
              );

        const baseClose =
          scheduleEnabled
            ? scheduleTiming?.baseCloseDate
            : (
                groupDeadline?.close_date ||
                instance.close_date
              );

        const effectiveClose =
          scheduleEnabled
            ? scheduleTiming
                ?.effectiveCloseDate
            : addDaysToDateString(
                baseClose,
                extensionDays
              );

        const feedback =
          latestFeedback(
            sub?.assignment_feedback
          );

        const today = getToday();

        const status =
          scheduleEnabled &&
          !scheduleTiming?.item
            ? (
                scheduleTiming?.choiceEnabled
                  ? 'not_chosen'
                  : 'not_configured'
              )
            : !baseOpen ||
              !effectiveClose
              ? 'not_configured'
              : today < baseOpen
                ? 'not_open'
                : today <= effectiveClose
                  ? 'open'
                  : 'closed';

        return (
          <td key={column.key} style={styles.tdCenter}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px'
              }}
            >
              {renderEffectiveInstructorBadge(
                enrolment,
                assignment.assigned_instructor_id
              )}

              <CellStatus
                status={status}
                submitted={!!sub?.submitted}
                uploadProblem={
                  assignmentSubmissionUploadProblem(
                    sub
                  )
                }
                graded={
                  !!feedback?.submitted ||
                  submissionHasUnpublishedGrade(
                    instructorFeedbackRows,
                    sub?.id
                  )
                }
                unpublished={
                  submissionHasUnpublishedGrade(
                    instructorFeedbackRows,
                    sub?.id
                  )
                }
                completionStatus={
                  gradingGridCompletionStatus(
                    instructorFeedbackRows,
                    'assignment',
                    sub?.id,
                    feedback
                  )
                }
                studentResponded={
                  feedbackHasStudentResponse(
                    feedback
                  )
                }
                onClick={() =>
                  openGradingTarget({
                    type: 'assignment',

                    // Data ownership belongs to the canonical
                    // group workspace owner.
                    enrolmentId:
                      submissionEnrolmentId,

                    gradeOverrideEnrolmentId:
                      enrolment.id,

                    assignmentId:
                      assignment.id,

                    structuredFormInstanceId:
                      instance.id,

                    structuredFormInstanceOrder:
                      instance.instance_order,

                    structuredFormOpenDate:
                      baseOpen,

                    structuredFormCloseDate:
                      baseClose,

                    structuredFormScheduleSourceLabel:
                      scheduleTiming?.source
                        ?.label || null,

                    structuredFormScheduleAnchorDate:
                      scheduleTiming?.source
                        ?.anchorDate || null,

                    // Presentation still identifies this as a
                    // group-owned submission.
                    assignmentGroupNumber:
                      groupContext.groupNumber
                  })
                }
              />
            </div>
          </td>
        );
      }

      const setting = getScheduleSetting(assignment);
      const scheduleEnabled =
        setting?.schedule_enabled === true;

      if (scheduleEnabled) {
        const item =
          getScheduleItemForEnrolment(
            enrolment,
            assignment
          );

        const choiceEnabled =
          isScheduleChoiceEnabledForAssignment({
            assignmentId: assignment.id,
            setting,
            context: assignmentScheduleContext,
            isMaster: false
          });

        if (!item) {
          return (
            <td key={column.key} style={styles.tdCenter}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px'
                }}
              >
                {renderEffectiveInstructorBadge(
                  enrolment,
                  assignment.assigned_instructor_id
                )}
                <CellStatus
                  status={choiceEnabled ? 'not_chosen' : 'not_configured'}
                  submitted={false}
                  graded={false}
                />
              </div>
            </td>
          );
        }

        const sub =
          getOccurrenceAssignmentSubmission(
            enrolment.id,
            assignment.id,
            item.id
          );

        const parentSub =
          getParentAssignmentSubmission(
            enrolment.id,
            assignment.id
          );

        const dates = getScheduleItemDates(
          item,
          setting,
          assignmentScheduleContext,
          parentSub?.extension_days || 0
        );

        const source = getScheduleItemSource(
          item,
          assignmentScheduleContext
        );

        const feedback =
          latestFeedback(
            sub?.assignment_feedback
          );

        return (
          <td key={column.key} style={styles.tdCenter}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px'
                }}
              >
                {renderEffectiveInstructorBadge(
                  enrolment,
                  assignment.assigned_instructor_id
                )}

                <CellStatus
                  status={getScheduleWindowStatus(dates)}
                  submitted={!!sub?.submitted}
                uploadProblem={
                  assignmentSubmissionUploadProblem(
                    sub
                  )
                }
                  graded={
                    !!feedback?.submitted ||
                    submissionHasUnpublishedGrade(
                      instructorFeedbackRows,
                      sub?.id
                    )
                  }
                  unpublished={
                    submissionHasUnpublishedGrade(
                      instructorFeedbackRows,
                      sub?.id
                    )
                  }
                completionStatus={
                  gradingGridCompletionStatus(
                    instructorFeedbackRows,
                    'assignment',
                    sub?.id,
                    feedback
                  )
                }
                studentResponded={
                  feedbackHasStudentResponse(
                    feedback
                  )
                }
                  allowOpenWithoutSubmission
                  onClick={() =>
                    openGradingTarget({
                      type: 'assignment',
                      enrolmentId: enrolment.id,
                      assignmentId: assignment.id,
                      scheduleItemId: item.id,
                      scheduleSourceTitle:
                        source?.title || 'Schedule item',
                      scheduleSourceMeta:
                        source?.meta || '',
                      scheduleOpenDate:
                        dates?.openDate || null,
                      scheduleDueDate:
                        dates?.dueDate || null
                    })
                  }
                />
              </div>

              <div style={styles.weekDate}>
                {source?.title || 'Schedule item'}
              </div>
            </div>
          </td>
        );
      }

      const sub =
        getParentAssignmentSubmission(
          enrolment.id,
          assignment.id
        );

      const feedback =
        latestFeedback(
          sub?.assignment_feedback
        );

      return (
        <td key={column.key} style={styles.tdCenter}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px'
            }}
          >
            {renderEffectiveInstructorBadge(
              enrolment,
              assignment.assigned_instructor_id
            )}

            <CellStatus
              status={getAssignmentStatus(
                assignment,
                sub
              )}
              submitted={!!sub?.submitted}
                uploadProblem={
                  assignmentSubmissionUploadProblem(
                    sub
                  )
                }
              graded={
                !!feedback?.submitted ||
                submissionHasUnpublishedGrade(
                  instructorFeedbackRows,
                  sub?.id
                )
              }
              unpublished={
                submissionHasUnpublishedGrade(
                  instructorFeedbackRows,
                  sub?.id
                )
              }
                completionStatus={
                  gradingGridCompletionStatus(
                    instructorFeedbackRows,
                    'assignment',
                    sub?.id,
                    feedback
                  )
                }
                studentResponded={
                  feedbackHasStudentResponse(
                    feedback
                  )
                }
              allowOpenWithoutSubmission
              onClick={() =>
                openGradingTarget({
                  type: 'assignment',
                  enrolmentId: enrolment.id,
                  assignmentId: assignment.id
                })
              }
            />
          </div>
        </td>
      );
    }

    return (
      <td key={column.key} style={styles.tdCenter}>
        —
      </td>
    );
  }

  if (loading) {
    return <div style={styles.muted}>Loading...</div>;
  }

  return (
    <div>
      {cqQuestionViewer && (
        <div
          style={
            styles.cqViewerBackdrop
          }
          onMouseDown={() =>
            setCqQuestionViewer(
              null
            )
          }
        >
          <div
            style={
              styles.cqViewer
            }
            onMouseDown={
              event =>
                event.stopPropagation()
            }
          >
            <div
              style={
                styles.cqViewerHeader
              }
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    flexWrap: 'wrap',
                    marginBottom: '9px'
                  }}
                >
                  <div
                    style={{
                      ...styles.cqViewerType,

                      marginBottom: 0,

                      ...(
                        cqQuestionViewer
                          .submission
                          ?.question_type ===
                        'Clarificatory'
                          ? styles.cqViewerTypeClarificatory
                          : styles.cqViewerTypeCritical
                      )
                    }}
                  >
                    {
                      cqQuestionViewer
                        .submission
                        ?.question_type ===
                      'Clarificatory'
                        ? 'Clarificatory Question'
                        : 'Critical Question'
                    }
                  </div>

                  <button
                    type="button"
                    style={
                      styles.cqViewerReassign
                    }
                    disabled={
                      cqReassigningId ===
                      cqQuestionViewer
                        .submission
                        ?.id
                    }
                    onClick={() =>
                      handleViewerCqReassign(
                        cqQuestionViewer
                          .submission
                      )
                    }
                  >
                    {
                      cqReassigningId ===
                      cqQuestionViewer
                        .submission
                        ?.id
                        ? 'Reassigning...'
                        : cqQuestionViewer
                            .submission
                            ?.question_type ===
                          'Clarificatory'
                          ? 'Reassign as Critical Question'
                          : 'Reassign as Clarificatory Question'
                    }
                  </button>
                </div>

                <div
                  style={
                    styles.cqViewerStudent
                  }
                >
                  {getStudentIdentity(
                    cqQuestionViewer
                      .enrolment
                      ?.students
                  )}
                </div>

                <div
                  style={
                    styles.cqViewerMeta
                  }
                >
                  Week {
                    cqQuestionViewer
                      .week
                      ?.week_number
                  }

                  {
                    (
                      cqQuestionViewer
                        .submission
                        ?.submitted_at ||
                      cqQuestionViewer
                        .submission
                        ?.created_at
                    )
                      ? (
                        ' · Received ' +
                        formatCqReceivedAt(
                          cqQuestionViewer
                            .submission
                            ?.submitted_at ||
                          cqQuestionViewer
                            .submission
                            ?.created_at
                        )
                      )
                      : ''
                  }
                </div>
              </div>

              <button
                type="button"
                style={
                  styles.cqViewerClose
                }
                onClick={() =>
                  setCqQuestionViewer(
                    null
                  )
                }
              >
                ×
              </button>
            </div>

            <div
              style={
                styles.cqViewerQuestion
              }
            >
              {
                cqQuestionViewer
                  .submission
                  ?.question_text ||
                'No question text is available.'
              }
            </div>
          </div>
        </div>
      )}



      {builtInExtension&&(
        <div
          style={
            styles
              .cqViewerBackdrop
          }
          onMouseDown={()=>
            !builtInExtension.saving &&
            setBuiltInExtension(
              null
            )
          }
        >
          <div
            style={{
              ...styles.cqViewer,
              maxWidth:'440px'
            }}
            onMouseDown={
              e=>
                e.stopPropagation()
            }
          >
            <div
              style={
                styles
                  .cqViewerHeader
              }
            >
              <div>
                <div
                  style={
                    styles
                      .classExtensionTitle
                  }
                >
                  Class Extension
                </div>

                <div
                  style={
                    styles
                      .cqViewerMeta
                  }
                >
                  {
                    builtInExtensionLabel(
                      builtInExtension
                        .column
                    )
                  }
                </div>
              </div>

              <button
                type="button"
                style={
                  styles
                    .cqViewerClose
                }
                disabled={
                  builtInExtension
                    .saving
                }
                onClick={()=>
                  setBuiltInExtension(
                    null
                  )
                }
              >
                ×
              </button>
            </div>

            <div
              style={
                styles
                  .extensionBox
              }
            >
              <div
                style={
                  styles
                    .extensionHeader
                }
              >
                Submission window
              </div>

              <div
                style={
                  styles
                    .extensionNote
                }
              >
                {
                  builtInExtension
                    .column
                    .kind==='pd'
                    ?'Applies the existing extension-days setting to every active student for this Philosophical Diary week.'
                    :'Extends this Self-Evaluation window for the whole class by the number of days below.'
                }
              </div>

              {builtInExtension
                .mixed&&(
                <div
                  style={
                    styles
                      .extensionNote
                  }
                >
                  Current extensions vary.
                  Saving here will replace
                  those values with the
                  class value below.
                </div>
              )}

              <div
                style={
                  styles
                    .extensionControls
                }
              >
                <label
                  style={
                    styles
                      .extensionLabel
                  }
                >
                  Extension (days)

                  <input
                    type="number"
                    min="0"
                    step="1"
                    style={
                      styles
                        .extensionInput
                    }
                    value={
                      builtInExtension
                        .days
                    }
                    onChange={
                      e=>
                        setBuiltInExtension(
                          v=>({
                            ...v,
                            days:
                              e.target
                                .value
                          })
                        )
                    }
                    disabled={
                      builtInExtension
                        .saving
                    }
                    autoFocus
                  />
                </label>

                <button
                  type="button"
                  style={
                    styles
                      .classExtensionSaveBtn
                  }
                  disabled={
                    builtInExtension
                      .saving
                  }
                  onClick={
                    saveBuiltInExtension
                  }
                >
                  {
                    builtInExtension
                      .saving
                      ?'Saving...'
                      :'Save Extension'
                  }
                </button>
              </div>

              {builtInExtension
                .error&&(
                <div
                  style={
                    styles
                      .classExtensionError
                  }
                >
                  {
                    builtInExtension
                      .error
                  }
                </div>
              )}

              {builtInExtension
                .notice&&(
                <div
                  style={
                    styles
                      .classExtensionNotice
                  }
                >
                  {
                    builtInExtension
                      .notice
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {classExtension && (
        <div
          style={styles.cqViewerBackdrop}
          onMouseDown={() => !classExtension.saving && setClassExtension(null)}
        >
          <div
            style={{ ...styles.cqViewer, maxWidth: '440px' }}
            onMouseDown={event => event.stopPropagation()}
          >
            <div style={styles.cqViewerHeader}>
              <div>
                <div style={styles.classExtensionTitle}>Class Extension</div>
                <div style={styles.cqViewerMeta}>{assignmentColumnLabel(classExtension.column)}</div>
              </div>
              <button
                type="button"
                style={styles.cqViewerClose}
                disabled={classExtension.saving}
                onClick={() => setClassExtension(null)}
              >
                ×
              </button>
            </div>

            <div style={styles.extensionBox}>
              <div style={styles.extensionHeader}>Submission window</div>
              <div style={styles.extensionNote}>
                Applies the existing extension-days setting to every active student/group for this assignment{classExtension.column.structuredFormInstance ? ' instance.' : '.'}
              </div>
              {classExtension.mixed && (
                <div style={styles.extensionNote}>
                  Current extensions vary. Saving here will replace those different values with the class value entered below.
                </div>
              )}

              <div style={styles.extensionControls}>
                <label style={styles.extensionLabel}>
                  {classExtension.column.structuredFormInstance ? 'Instance extension (days)' : 'Extension (days)'}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    style={styles.extensionInput}
                    value={classExtension.days}
                    onChange={event => setClassExtension(value => ({ ...value, days: event.target.value }))}
                    disabled={classExtension.saving}
                    autoFocus
                  />
                </label>
                <button
                  type="button"
                  style={styles.classExtensionSaveBtn}
                  disabled={classExtension.saving}
                  onClick={saveClassExtension}
                >
                  {classExtension.saving ? 'Saving...' : 'Save Extension'}
                </button>
              </div>

              {classExtension.error && <div style={styles.classExtensionError}>{classExtension.error}</div>}
              {classExtension.notice && <div style={styles.classExtensionNotice}>{classExtension.notice}</div>}
            </div>
          </div>
        </div>
      )}

      {gradingTarget && (
        <GradingPanel
          target={gradingTarget}
          course={course}
          session={gradingSession}
          enrolments={enrolments}
          weeks={weeks}
          cqSubmissions={cqSubmissions}
          pdSubmissions={pdSubmissions}
          assignmentSubmissions={assignmentSubmissions}
          selfEvalResponses={selfEvalResponses}
          courseAssignments={courseAssignments}
          onSubmitPrevious={
            previousUngradedTarget
              ? () =>
                  setGradingTarget(
                    previousUngradedTarget
                  )
              : null
          }
          onSubmitNext={
            nextUngradedTarget
              ? () =>
                  setGradingTarget(
                    nextUngradedTarget
                  )
              : null
          }
          onClose={() => {
            setGradingTarget(null);
            refreshData();
          }}
          onUpdate={(type, data) => {
            if (type === 'cq') setCqSubmissions(data);
            if (type === 'pd') setPdSubmissions(data);
            if (type === 'assignment') {
              setAssignmentSubmissions(data);
            }
            if (type === 'selfeval') {
              setSelfEvalResponses(data);
            }

            refreshData();
          }}
        />
      )}

      <div style={styles.typeSelector}>
        <label style={styles.label}>Assignment type</label>

        <div style={styles.typeButtons}>
          {getBuiltInTypes(course).map(type => (
            <button
              key={type}
              type="button"
              style={{
                ...styles.typeBtn,
                ...(selectedType === type
                  ? styles.typeBtnActive
                  : {})
              }}
              onClick={event => {
                if (event.detail > 0) {
                  event.currentTarget.blur();
                }
                handleTabChange(type);
              }}
            >
              {type}
            </button>
          ))}

          {assignmentGroups.map(group => {
            const tabKey = getAssignmentTabKey(group.key);

            return (
              <button
                key={tabKey}
                type="button"
                style={{
                  ...styles.typeBtn,
                  ...(selectedType === tabKey
                    ? styles.typeBtnActive
                    : {})
                }}
                onClick={event => {
                  if (event.detail > 0) {
                    event.currentTarget.blur();
                  }
                  handleTabChange(tabKey);
                }}
              >
                {group.name}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '0 0 10px'
        }}
      >
        <button
          type="button"
          onClick={() =>
            refreshData()
          }
          style={{
            border:
              '1px solid #cbd5df',
            borderRadius: '6px',
            background: 'white',
            color: '#526575',
            padding: '6px 10px',
            fontSize: '11px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          Refresh data
        </button>
      </div>

      {refreshError && (
        <div style={styles.refreshWarning}>
          {refreshError}
        </div>
      )}

      {selectedType === 'Attendance' && (
        <div style={styles.attendancePrintBar}>
          <label style={styles.attendancePrintLabel}>
            Print Attendance Sheet
          </label>

          <select
            style={styles.attendancePrintSelect}
            value={attendancePrintWeekId}
            onChange={event =>
              setAttendancePrintWeekId(
                event.target.value
              )
            }
          >
            <option value="all">All</option>
            {getPrintableAttendanceWeeks().map(
              week => (
                <option
                  key={week.id}
                  value={week.id}
                >
                  {formatAttendanceSheetWeek(week)}
                </option>
              )
            )}
          </select>

          {selectedAttendanceHasTimeOverride() && (
            <select
              style={styles.attendancePrintSelect}
              value={attendancePrintStartMode}
              onChange={event =>
                setAttendancePrintStartMode(
                  event.target.value
                )
              }
            >
              <option value="all">
                All Students
              </option>

              {getAttendanceStartTimeOptions().map(
                time => (
                  <option
                    key={time}
                    value={`time:${time}`}
                  >
                    {time}
                  </option>
                )
              )}

              <option value="separate">
                Separate by Start Time
              </option>
            </select>
          )}

          <button
            type="button"
            style={styles.attendancePrintBtn}
            disabled={attendancePrinting}
            onClick={handlePrintAttendanceSheet}
          >
            {attendancePrinting
              ? 'Generating...'
              : 'Print Attendance Sheet'}
          </button>
        </div>
      )}

      {selectedType === 'Critical Questions' &&
        course.cq_clarificatory_enabled === true && (
          <div style={styles.attendancePrintBar}>
            <label
              style={
                styles.attendancePrintLabel
              }
            >
              Print Clarificatory Questions
            </label>

            <select
              style={
                styles.attendancePrintSelect
              }
              value={
                clarificatoryPrintWeekId
              }
              onChange={
                event =>
                  setClarificatoryPrintWeekId(
                    event.target.value
                  )
              }
            >
              <option value="all">
                All
              </option>

              {getPrintableClarificatoryWeeks()
                .map(
                  week => (
                    <option
                      key={
                        week.id
                      }
                      value={
                        week.id
                      }
                    >
                      {formatAttendanceSheetWeek(
                        week
                      )}
                    </option>
                  )
                )}
            </select>

            <button
              type="button"
              style={
                styles.attendancePrintBtn
              }
              disabled={
                clarificatoryPrinting
              }
              onClick={
                handlePrintClarificatoryQuestions
              }
            >
              {clarificatoryPrinting
                ? 'Generating...'
                : 'Print Clarificatory Questions'}
            </button>
          </div>
        )}

      {selectedType === 'Question Improvement' ? (
        <QuestionImprovement course={course} session={gradingSession} />
      ) : (
        <>
          <div style={styles.refreshMeta}>
            Auto-refreshing every 30 seconds and when this browser tab regains focus. Passive refresh pauses while a grading form is open.
          </div>

          <div
            ref={
              horizontalScroll
                .topScrollRef
            }
            style={{
              ...styles
                .topHorizontalScroll,

              left:
                horizontalScroll
                  .floatingLeft +
                'px',

              width:
                horizontalScroll
                  .floatingWidth +
                'px',

              bottom:
                horizontalScroll
                  .floatingBottom +
                'px',

              ...(
                horizontalScroll
                  .needsScroll &&
                horizontalScroll
                  .floatingVisible
                  ? {}
                  : styles
                      .topHorizontalScrollHidden
              )
            }}
            onScroll={
              horizontalScroll
                .onTopScroll
            }
            aria-hidden={
              !(
                horizontalScroll
                  .needsScroll &&
                horizontalScroll
                  .floatingVisible
              )
            }
          >
            <div
              style={{
                ...styles
                  .topHorizontalScrollTrack,

                width:
                  Math.max(
                    1,
                    horizontalScroll
                      .scrollWidth
                  ) +
                  'px'
              }}
            />
          </div>

          <div
            ref={
              horizontalScroll
                .contentScrollRef
            }
            style={
              styles.tableWrap
            }
            onScroll={
              horizontalScroll
                .onContentScroll
            }
          >
            <table
              style={{
                ...styles.table,
                minWidth: `${Math.max(
                  520,
                  240 + columns.length * 105 + (showStudentInstructorColumn ? 42 : 0)
                )}px`
              }}
            >
              <thead>
                <tr>
                  {showStudentInstructorColumn && (
                    <th
                      style={styles.thInstructor}
                      title="Assigned instructor"
                    >
                      I
                    </th>
                  )}
                  <th
                    style={{
                      ...styles.thName,
                      left:
                        showStudentInstructorColumn
                          ? '42px'
                          : 0
                    }}
                  >
                    Student
                  </th>
                  {columns.map(renderColumnHeader)}
                </tr>
              </thead>

              <tbody>
                {enrolments.map(enrolment => {
                  const instructorIndex = instructors.findIndex(
                    instructor =>
                      instructor.id === enrolment.assigned_instructor_id
                  );
                  const assignedInstructor =
                    instructorIndex >= 0 ? instructors[instructorIndex] : null;
                  const badgeStyle = instructorIndex >= 0
                    ? INSTRUCTOR_BADGES[
                        instructorIndex % INSTRUCTOR_BADGES.length
                      ]
                    : null;

                  return (
                    <tr key={enrolment.id}>
                      {showStudentInstructorColumn && (
                        <td style={styles.tdInstructor}>
                          {assignedInstructor ? (
                            <span
                              style={{
                                ...styles.instructorBadge,
                                ...badgeStyle
                              }}
                              title={assignedInstructor.name}
                            >
                              {getInstructorInitials(assignedInstructor.name)}
                            </span>
                          ) : (
                            <span
                              style={styles.unassignedInstructor}
                              title="No instructor assigned"
                            >
                              —
                            </span>
                          )}
                        </td>
                      )}

                      <td
                        style={{
                          ...styles.tdName,
                          left:
                            showStudentInstructorColumn
                              ? '42px'
                              : 0
                        }}
                        title={getStudentIdentity(enrolment.students)}
                      >
                        <div style={styles.studentPrimary}>
                          {
                            enrolment.students?.english_name ||
                            enrolment.students?.chinese_name ||
                            enrolment.students?.student_code ||
                            '—'
                          }
                        </div>

                        <div style={styles.studentSecondary}>
                          {[
                            enrolment.students?.student_code,
                            enrolment.students?.english_name &&
                            enrolment.students?.chinese_name
                              ? enrolment.students.chinese_name
                              : null
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>

                      {columns.map(column =>
                        renderCell(enrolment, column)
                      )}
                    </tr>
                  );
                })}

                {enrolments.length === 0 && (
                  <tr>
                    <td
                      colSpan={Math.max(
                        1,
                        columns.length + 1 + (showStudentInstructorColumn ? 1 : 0)
                      )}
                      style={styles.emptyCell}
                    >
                      No active students.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '8px 0'
  },

  typeSelector: {
    position: 'sticky',
    top: '44px',
    zIndex: 24,
    margin: '-10px 0 14px',
    padding: '10px 0 8px',
    background: 'white',
    borderBottom: '1px solid #edf1f4'
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '8px',
    color: '#1f2933'
  },

  typeButtons: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '8px',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '4px',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain'
  },

  typeBtn: {
    border: '1px solid #d7dee5',
    background: 'white',
    borderRadius: '8px',
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#1f2933',
    flexShrink: 0,
    whiteSpace: 'nowrap'
  },

  typeBtnActive: {
    background: '#215b7d',
    borderColor: '#215b7d',
    color: 'white'
  },

  refreshMeta: {
    fontSize: '11px',
    color: '#8a98a5',
    marginBottom: '10px'
  },

  refreshWarning: {
    fontSize: '12px',
    color: '#8a5b00',
    background: '#fff4d7',
    border: '1px solid #ead49b',
    borderRadius: '7px',
    padding: '7px 10px',
    marginBottom: '8px'
  },

  attendancePrintBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '12px',
    padding: '10px 12px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    background: '#f7f9fa'
  },

  attendancePrintLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933'
  },

  attendancePrintSelect: {
    minWidth: '220px',
    border: '1px solid #cbd5df',
    borderRadius: '6px',
    padding: '6px 8px',
    background: 'white',
    fontSize: '13px'
  },

  attendancePrintBtn: {
    border: 'none',
    borderRadius: '6px',
    padding: '7px 12px',
    background: '#215b7d',
    color: 'white',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  topHorizontalScroll: {
    position: 'fixed',
    zIndex: 190,
    overflowX: 'auto',
    overflowY: 'hidden',
    height: '18px',
    border:
      '1px solid #b8c5ce',
    borderRadius: '6px',
    background: 'white',
    boxSizing: 'border-box',
    boxShadow:
      '0 -2px 8px rgba(31, 41, 51, 0.12)',
    WebkitOverflowScrolling:
      'touch',
    overscrollBehaviorX:
      'contain'
  },

  topHorizontalScrollHidden: {
    display: 'none'
  },

  topHorizontalScrollTrack: {
    height: '1px'
  },

  tableWrap: {
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    minWidth: '520px'
  },

  thInstructor: {
    textAlign: 'center',
    padding: '9px 4px',
    fontSize: '11px',
    color: '#66788a',
    background: '#f0f4f7',
    borderBottom: '1px solid #d7dee5',
    width: '34px',
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 6
  },

  tdInstructor: {
    textAlign: 'center',
    padding: '6px 3px',
    borderBottom: '1px solid #d7dee5',
    width: '34px',
    background: 'white',
    position: 'sticky',
    left: 0,
    zIndex: 3
  },

  instructorBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '26px',
    height: '22px',
    padding: '0 4px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: '800',
    letterSpacing: '0.02em'
  },

  unassignedInstructor: {
    color: '#b6c0c8',
    fontSize: '12px'
  },

  thName: {
    textAlign: 'left',
    padding: '9px 11px',
    fontSize: '13px',
    color: '#42515f',
    background: '#f0f4f7',
    borderBottom: '1px solid #d7dee5',
    width: '240px',
    position: 'sticky',
    top: 0,
    zIndex: 6,
    boxShadow: '2px 0 4px rgba(31, 41, 51, 0.08)'
  },

  thWeek: {
    textAlign: 'center',
    padding: '7px 6px',
    fontSize: '12px',
    color: '#42515f',
    background: '#f0f4f7',
    borderBottom: '1px solid #d7dee5',
    minWidth: '92px',
    position: 'sticky',
    top: 0,
    zIndex: 5
  },

  weekDate: {
    fontSize: '11px',
    color: '#66788a',
    marginTop: '2px',
    fontWeight: '400'
  },

  publishGradesBtn: {
    marginTop: '5px',
    border: '1px solid #215b7d',
    borderRadius: '5px',
    background: 'white',
    color: '#215b7d',
    padding: '3px 6px',
    fontSize: '10px',
    fontWeight: '800',
    cursor: 'pointer'
  },

  tdName: {
    padding: '8px 11px',
    borderBottom: '1px solid #d7dee5',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    verticalAlign: 'middle',
    lineHeight: '1.25',
    background: 'white',
    position: 'sticky',
    zIndex: 3,
    boxShadow: '2px 0 4px rgba(31, 41, 51, 0.08)'
  },

  studentPrimary: {
    color: '#1f2933',
    fontWeight: '700'
  },

  studentSecondary: {
    marginTop: '3px',
    color: '#66788a',
    fontSize: '11px',
    fontWeight: '500',
    lineHeight: '1.25'
  },

  tdCenter: {
    padding: '6px',
    borderBottom: '1px solid #d7dee5',
    textAlign: 'center',
    verticalAlign: 'middle'
  },

  emptyCell: {
    padding: '18px',
    textAlign: 'center',
    color: '#66788a',
    fontSize: '13px'
  },

  cell: {
    border: 'none',
    borderRadius: '6px',
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'default',
    whiteSpace: 'nowrap',
    maxWidth: '100%'
  },

  cellClickable: {
    cursor: 'pointer'
  },

  cellGraded: {
    background: '#e7f5ed',
    color: '#216e45'
  },

  cellUnpublished: {
    background: '#fff4d7',
    color: '#8a5b00'
  },

  cellIncomplete: {
    background: '#fff4d7',
    color: '#8a5b00'
  },

  cellContent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px'
  },

  studentReplyMarker: {
    color: '#215b7d',
    fontSize: '10px',
    fontWeight: '800',
    lineHeight: 1,
    opacity: 0.8
  },

  cellSubmitted: {
    background: '#e7f1f7',
    color: '#215b7d',
    cursor: 'pointer'
  },

  cellNotYet: {
    background: '#fff4d7',
    color: '#8a5b00'
  },

  cellMissing: {
    background: '#faeaea',
    color: '#a12a2a'
  },

  cellNotOpen: {
    background: '#f0f4f7',
    color: '#66788a'
  },

  cellDisabled: {
    background: 'transparent',
    color: '#d7dee5'
  },

  attBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  attBtnOn: {
    background: '#215b7d',
    borderColor: '#215b7d',
    color: 'white'
  },

  attBtnOff: {
    background: 'white',
    borderColor: '#d7dee5',
    color: 'transparent'
  },

  futureAtt: {
    color: '#d7dee5',
    fontSize: '13px'
  },

  qiHeader: {
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px'
  },

  qiLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#66788a'
  },

  qiAssignBtn: {
    background: '#233746',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '2px 6px',
    fontSize: '10px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  cellStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '3px'
  },

  cqViewButton: {
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontSize: '11px',
    fontWeight: '800',
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  cqViewCritical: {
    background: '#d97706'
  },

  cqViewClarificatory: {
    background: '#2878b5'
  },

  cqManualMark: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    color: '#66788a',
    fontSize: '9px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },

  assignmentHeaderLine: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px'
  },
  classExtensionPlus: {
    width: '20px',
    height: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #b8c5ce',
    borderRadius: '50%',
    background: 'white',
    color: '#215b7d',
    fontSize: '15px',
    fontWeight: '700',
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer'
  },
  classExtensionTitle: {
    color: '#1f2933',
    fontSize: '17px',
    fontWeight: '700'
  },
  extensionBox: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    background: '#f7f9fa',
    padding: '14px'
  },
  extensionHeader: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '8px'
  },
  extensionControls: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    marginTop: '12px'
  },
  extensionLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a'
  },
  extensionInput: {
    width: '110px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '14px',
    background: 'white'
  },
  extensionNote: {
    fontSize: '12px',
    color: '#215b7d',
    marginTop: '9px',
    lineHeight: '1.45'
  },
  classExtensionSaveBtn: {
    background: 'transparent',
    color: '#66788a',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  classExtensionError: {
    marginTop: '10px',
    color: '#a12a2a',
    fontSize: '12px'
  },
  classExtensionNotice: {
    marginTop: '10px',
    color: '#216e45',
    fontSize: '12px'
  },

  cqViewerBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1500,
    background:
      'rgba(15, 23, 42, 0.38)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '12px',
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch'
  },

  cqViewer: {
    width: 'min(720px, 94vw)',
    margin: '0 auto',
    overflow: 'visible',
    background: 'white',
    borderRadius: '12px',
    boxShadow:
      '0 20px 55px rgba(15, 23, 42, 0.25)',
    padding: '22px'
  },

  cqViewerHeader: {
    position: 'sticky',
    top: '-22px',
    zIndex: 2,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '18px',
    margin: '-22px -22px 24px',
    padding: '22px',
    background: 'white',
    borderBottom: '1px solid #edf1f4',
    borderRadius: '12px 12px 0 0'
  },

  cqViewerType: {
    display: 'inline-block',
    borderRadius: '999px',
    padding: '4px 9px',
    color: 'white',
    fontSize: '11px',
    fontWeight: '800',
    marginBottom: '9px'
  },

  cqViewerTypeCritical: {
    background: '#d97706'
  },

  cqViewerTypeClarificatory: {
    background: '#2878b5'
  },

  cqViewerReassign: {
    border:
      '1px solid #bcccdc',
    borderRadius:
      '5px',
    background:
      'white',
    color:
      '#334e68',
    padding:
      '5px 9px',
    fontSize:
      '11px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  cqViewerStudent: {
    color: '#1f2933',
    fontSize: '15px',
    fontWeight: '700'
  },

  cqViewerMeta: {
    marginTop: '4px',
    color: '#66788a',
    fontSize: '11px'
  },

  cqViewerClose: {
    border: 'none',
    background: 'transparent',
    color: '#52606d',
    fontSize: '25px',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 3px'
  },

  cqViewerQuestion: {
    color: '#1f2933',
    fontSize: '22px',
    lineHeight: '1.55',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    padding: '8px 4px 16px'
  },

  qiStatus: {
    borderRadius: '999px',
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: '700'
  },

  qiSubmitted: {
    background: '#e7f5ed',
    color: '#216e45'
  },

  qiDraft: {
    background: '#fff4d7',
    color: '#8a5b00'
  },

  qiAssigned: {
    background: '#f0f4f7',
    color: '#66788a'
  },

  qiNotInSubmission: {
    background: '#faeaea',
    color: '#a12a2a'
  },

  qiNotAssigned: {
    background: 'transparent',
    color: 'transparent'
  }
};
